import { createDraftSafeSelector } from '@reduxjs/toolkit';
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import enums from '../utils/enums';
import http from '../utils/http';
import utilityHelper from '../utils/utilityHelper';
import { AuditorReference, ContactInfo } from "./AuditorSlice";
import { Concept, DimensionalConceptRef } from "./ConceptSlice";
import { DocumentRelationship } from "./DocumentsSlice";
import { EditEntityQuery, Entity, EntityReference } from "./EntitySlice";
import { AfrEvents, getCommentEventList, getMessageEventList } from './EventsSlice';
import { RootState } from './store';
import { Reference } from './TaxonomyReferenceSlice';
import { LogicalPeriodEnum, TaxonomyTypeEnum } from './TaxonomyVersionSlice';
import { logerUser } from "./UserSlice";



const ReportingTypeAxisName = "ReportingTypeAxis";

export interface JLACDate {
    dateType: "WITHHOLDING_START_DATES" | "BEGIN_ENFORCEMENT_LETTER_ISSUE_DATE";
    dateValue: string | null ;
}

export interface Axis {
    name: string;
    code: string;
    id: string;
    documentation: string;
    references: Array<Reference>;
    membersByYear: Array<Concept>;
    typedDomain: Concept | null;
    domainDatatype: string;
}
export interface ConceptReference {
    id: string;
}

export interface AuditMessage {
    requireAudit: boolean,
    message: string
}

export interface DocumentListEntry {
    document: DocumentRelationship,
    type: string,
    name: string,
}

// Expanded to include severity (INFO, WARNING, ERROR) and category
/*
public enum ValidationErrorCategory {
    ContextPeriodNoStartTime = 0,
    //
    // Summary:
    //     Context peroid type is duration, but has no end date.
    ContextPeriodNoEndTime = 1,
    //
    // Summary:
    //     Context peroid type is duration, but end date is before start date.
    ContextPeriodStartAfterEnd = 2,
    //
    // Summary:
    //     Context peroid type is instant, but has no instant date.
    ContextInstantNoTime = 3,
    //
    // Summary:
    //     Context scenario can't have xbrl namespace node.
    ContextScenarioXbrlNamespace = 4,
    //
    // Summary:
    //     Context scenario can't have element in the substitution group for elements defined
    //     in the xbrl namespace.
    ContextScenarioXbrlSubstitutionGroup = 5,
    //
    // Summary:
    //     Context scenario can't be empty"
    ContextScenarioEmpty = 6,
    //
    // Summary:
    //     Context segment can't have xbrl namespace node.
    ContextSegmentXbrlNamespace = 7,
    //
    // Summary:
    //     Context segment can't have element in the substitution group for elements defined
    //     in the xbrl namespace.
    ContextSegmentXbrlSubstitutionGroup = 8,
    //
    // Summary:
    //     Context segment can't be empty
    ContextSegmentEmpty = 9,
    //
    // Summary:
    //     Item must have a context.
    ItemNoContext = 10,
    //
    // Summary:
    //     Item has period type conflict with context.
    ItemPeroidTypeConflictWithContext = 11,
    //
    // Summary:
    //     Item is numeric and must have a unit.
    ItemNumericNoUnit = 12,
    //
    // Summary:
    //     Item is monetary type and must have a single unit measure.
    MonetaryItemNoSingleUnitMeasure = 13,
    //
    // Summary:
    //     Item is monetary type and must have a Iso 4217 style unit measure.
    MonetaryItemNoISO4217 = 14,
    //
    // Summary:
    //     Item is share type and must have a single unit measure.
    ShareItemNoSingleUnitMeasure = 15,
    //
    // Summary:
    //     Item is share type and must have a xbrli:shares unit measure.
    ShareItemNoShareUnitMeasure = 16,
    //
    // Summary:
    //     Item is nil and must not have either precision or decimals.
    NillItemWithPrecisionOrDecimals = 17,
    //
    // Summary:
    //     Item is fraction type and must not have either precision or decimals.
    FractionItemWithPrecisionOrDecimals = 18,
    //
    // Summary:
    //     Item is numeric type and must not have both precision and decimals.
    NumericItemWithBothPrecisionAndDecimals = 19,
    //
    // Summary:
    //     Item is numeric type and must have either precision or decimals.
    NumericItemWithoutPrecisionOrDecimals = 20,
    //
    // Summary:
    //     Item is not numeric type and must not have either precision or decimals.
    NonNumericItemWithPrecisionOrDecimals = 21,
    //
    // Summary:
    //     Footnote arc can't find from Loc.
    FootnoteArcFromNotFound = 22,
    //
    // Summary:
    //     Footnote arc can't find to footnote.
    FootnoteArcToNotFound = 23,
    INVALID_CONCEPT,
    INELIGIBLE_ACCOUNT,
    INVALID_PERIOD,
    INVALID_DIMENSION,
    EENUM_VALUE_NOT_FOUND,
    INVALID_DATA_FORMAT,
    FORMULA_ERROR,
    REQUIRED_CONCEPT_NOT_FOUND
}
*/

export const CRA: string = "Community Redevelopment Agencies";

export interface ValidationError {
    severity: string;
    category: string;
    message: string;
}
export interface Period {
    startDate: string | null;
    endDate: string | null;
    instantDate: string | null;
}
export interface Fact {
    dimensions: Array<DimensionReference>;
    concept: ConceptReference;
    value: number;
    textValue: string | null;
    accountCode: string;
    validationerrors: Array<ValidationError>;
}
export interface UnassignedFact {
    dimensions: Array<DimensionReference>;
    concept: Concept;
    value: number;
}
export interface DimensionIdReference {
    axis: string;
    member: string | null;
    typedContent: string | null;
}

export interface ErrorFactReference {
    dimensions: Array<DimensionIdReference>;
    concept: ConceptReference;
    value: number;
    textValue: string | null;
}

export interface EventGeneric {
    user: string;
    type: string;
    date: string;
    detail: string;
}
export interface DimensionReference {
    axis: Axis;
    member: Concept | null;
    typedContent: string | null;
}
export interface Account {
    name: string;
    code: string;
    id: string;
}

export interface TableBreakdownEntry {
    label: string;
    conceptaspect: Concept;
    dimensionaspect: DimensionReference;
    firstDimension: DimensionReference;
    dimensions: Array<Concept>;
    axes: Array<Axis>;
    sublevels: Array<TableBreakdownEntry>;
}
export interface FactTableRow {
    account: Account | null;
    dimensions: Array<DimensionReference>;
    facts: Array<Fact>;
}
export interface Table {
    name: string;
    rowHierarchy: TableBreakdownEntry;
    columnHierarchy: TableBreakdownEntry;
    sliceHierarchy: TableBreakdownEntry | null;
    nthlevelcolumnHierarchy: Array<TableBreakdownEntry>;
    nthlevelrowHierarchy: Array<TableBreakdownEntry>;
    nthlevelsliceHierarchy: Array<TableBreakdownEntry>;
    rows: Array<Concept>;
    documentationByYear: string;
}

export interface FactTable {
    structure: Table;
    rows: Array<FactTableRow>;
}
export interface MatchTableFacts {
    matchConcept: DimensionalConceptRef;
    tableName: string;
}

// Audit and Certification model
export interface Audit {
    auditorReference: AuditorReference | null;
    auditPdf: DocumentRelationship | null;
    datePerformed: string | null;
    dateReceived: string | null;
    auditType: string | null;
    sendAuditPdf: boolean | null;
}
export interface Certifier {
    title: string;
    name: string;
    agree: boolean | null;
    contactInfo: ContactInfo | null;
}

export interface Certification {
    financialEmergency: boolean | null;
    compliance218: string | null;
    financialDeterioration: boolean | null;
    submittedDocumentation: string | null;
    icertify: boolean | null;
    firstCertifier: Certifier | null;
    secondCertifier: Certifier | null;
}

// Event model
export interface AfrVersionBooleanStateChange {
    creator: logerUser;
    timestamp: string;
    payload: string;
    state: string;
    statevalue: boolean;
}
export interface AfrVersionStateChange {
    creator: logerUser;
    timestamp: string;
    version: string;
    statevalue: string;
}
export interface AfrVersionEmailEvent {
    creator: logerUser;
    timestamp: string;
    payload: string;
    recipient: logerUser;
    subject: string;
    body: string;
}
export interface AfrAssignmentEvent {
    creator: logerUser;
    timestamp: string;
    payload: string;
    assignee: logerUser;

}
export interface AfrVersionBooleanStateChangeInput {
    creatorId: string;
    timestamp: string;
    state: string;
    statevalue: boolean;
}
export interface AfrVersionStateChangeInput {
    creatorId: string;
    timestamp: string;
    statevalue: string;
}
export interface VersionDocument {
    documentId: string;
    role: string;
    timestamp: string;
}
export interface WorkState {
    balanceSheetComplete: boolean | null;
    revenuesComplete: boolean | null;
    expendituresComplete: boolean | null;
    noBalanceSheet: boolean | null;
    noRevenues: boolean | null;
    noExpenditures: boolean | null;
    noExceptionsWaivers: boolean | null;
    noConstructionProjects: boolean | null;
    noBonds: boolean | null;
    governmentalFundsComplete: boolean | null;
    proprietaryFundsComplete: boolean | null;
    fiduciaryFundsComplete: boolean | null;
    componentUnitsFundsComplete: boolean | null;
    noGovernmentalFunds: boolean | null;
    noProprietaryFunds: boolean | null;
    noFiduciaryFunds: boolean | null;
    noComponentUnitsFunds: boolean | null;
}
export interface ReviewState {
    generalInfoReviewed: boolean | null;
    balanceSheetReviewed: boolean | null;
    expendituresReviewed: boolean | null;
    revenuesReviewed: boolean | null;
    debtReviewed: boolean | null;
    componentUnitsReviewed: boolean | null;
    auditReviewed: boolean | null;
    certificationReviewed: boolean | null;
    supplementalReviewed: boolean | null;
    cr939CourtInnovationsReviewed: boolean | null;
    cr939LegalAidProgramReviewed: boolean | null;
    cr939LawLibraryReviewed: boolean | null;
    cr939JuvenileAlternativeProgramReviewed: boolean | null;
    cr939SummaryReviewed: boolean | null;
    cr29PartAReviewed: boolean | null;
    cr29PartBReviewed: boolean | null;
    cr29PartCReviewed: boolean | null;
    cr29PartDReviewed: boolean | null;
    cr29PartEReviewed: boolean | null;
    cr29CertificationReviewed: boolean | null;
    governmentalFundsReviewed: boolean | null;
    proprietaryFundsReviewed: boolean | null;
    fiduciaryFundsReviewed: boolean | null;
    componentUnitsFundsReviewed: boolean | null;
}
export interface AdminState {
    adminReconciled: boolean | null;
    jLACNonCompliance: boolean | null;
    versionStatus: string | null;
}
export interface versionState {
    workState: WorkState;
    reviewState: ReviewState;
    adminState: AdminState;
}
export interface AfrVersion {
    entityId: string;
    year: number;
    status: string;
    audittype: string;
    versionNumber: number;
    amendmentNumber: number | null;
    revisionNumber: number | null;
    versionState: versionState;
    tables: Array<FactTable>;
    validationerrors: Array<ValidationError>;
    unassignedFacts: Array<Fact> | null;
    invalidFacts: Array<Fact> | null;
    missingFacts: Array<Fact> | null;
    events: Array<AfrVersionBooleanStateChange>;
    statusEvents: Array<AfrVersionStateChange>;
    documents: Array<DocumentRelationship>;
    audit: Audit;
    certification: Certification;
    withholdingStartDates: string | null;
    beginEnforcementLetterIssueDate: string | null;
}

export interface NewAccount {
    tableId: string;
    account: Account;
    dimensions: Array<DimensionReference>;
}

export interface Afr {
    afrId: string;
    year: number;
    status: string;
    taxonomyType: TaxonomyTypeEnum | null;
    logicalPeriod: LogicalPeriodEnum | null;
    audittype: string;
    calculations: string;
    currentversion: AfrVersion;
    affiliateReporting: Array<AffiliateReporting> | null;
    affiliateReportingForAfr: AffiliateReporting | null;
    lastPriorAuditYear: number | null;
    afrReceivedDate: string | null;
    auditReceivedDate: string | null;
    requestDate: string | null;
    owner: EntityReference | null;
}

export interface factValue {
    tableId: string;
    rowId: string;
    fundGroup: string;
    concept: string;
    value: string;
}

export interface bulkFactValues {
    tableId: string;
    fundGroup: string | null;
    sliceId: string | null;
    minAccount: string | null;
    maxAccount: string | null;
    facts: factValue[];
}

export interface bulkReassignColumnDimension {
    tableId: string;
    sliceId: string | null;
    axisId: string;
    prevId: string;
    newId: string;
}

export interface unassignedFactValue {
    accountCode: string;
    dimensionalSignature: string;
    concept: string;
    value: string;
}

export interface afrAttributeValue {
    affiliateId: string;
    attributePath: string;
    valueType: string;
    stringValue: string;
    boolValue: boolean;
    numberValue: number;
}

export interface versionStateValue {
    propGroup: string;
    propName: string;
    boolValue: boolean | null;
    value: string | null;
}

export interface noDataValue {
    affiliate: string;
    propGroup: string;
    propName: string;
    boolValue: boolean | null;
}

export interface DocumentReferenceInput {
    documentId: string;
    role: string;
    affiliate: string | null;
}
export interface AuditInput {
    datePerformed: string | null;
    auditorId: string | null;
    auditPdf: DocumentRelationshipInput | null;
    auditType: string | null;
    sendAuditPdf: boolean | null;
}

export interface CertifierInput {
    title: string;
    name: string;
    agree: boolean;
}

export interface CertificationInput {
    financialEmergency: boolean | null;
    compliance: string;
    financialDeterioration: boolean | null;
    submittedDocumentation: string;
    icertify: boolean | null;
    first: CertifierInput | null;
    second: CertifierInput | null;

}

export interface DimensionReferenceInput {
    axisId: string;
    memberId: string | null;
    typedContent: string | null;
}
export interface FactInput {
    conceptId: string;
    dimensions: Array<DimensionReferenceInput>;
    decimalValue: number;
}
export interface DocumentRelationshipInput {
    documentId: string | null;
    role: string;
    timestamp: string | null;
    affiliate: string | null;
}

export interface EntityDocumentSave {
    afrYear: number;
    primaryEntityCode: string;
    documentRelationship: DocumentRelationshipInput;
}

export interface AfrVersionInput {
    versionNumber: number;
    amendmentNumber: number | null;
    revisionNumber: number | null;
    audit: AuditInput | null;
    certification: CertificationInput | null;
    facts: Array<FactInput>;
    stateChangeEvents: Array<AfrVersionBooleanStateChangeInput>;
    statusChangeEvents: Array<AfrVersionStateChangeInput>;
    changedVersionStates: versionState;
    documents: Array<DocumentRelationshipInput>;
    withholdingStartDates: string | null;
    beginEnforcementLetterIssueDate: string | null;
}

export interface AffiliateReporting {
    affiliateReference: EntityReference | null;
    reportingType: string;
    audit: Audit | null;
    auditReceivedDate: string | null;
    affiliateReportingForAfr: AffiliateReporting | null;
    independentTotals: Array<Fact> | null;
}

export interface AffiliateReportingInput {
    affiliateCode: string;
    reportingType: string;
    audit: AuditInput | null;
    auditReceivedDate: string | null;
}

export interface AfrInput {
    afrId: string;
    year: number;
    affiliateReporting: Array<AffiliateReportingInput> | null;
    version: AfrVersionInput;
    submit: boolean;
    auditReceivedDate: string | null;
    requestDate: string | null;
}

export interface CommitAfrRequest {
    entityId: string;
    afrYear: number;
    afrToSave: AfrInput | null;
}

export interface AfrState {
    afr: Afr | null;
    versionPropertiesChanged: versionState | null;
    status: 'idle' | 'loading' | 'failed' | 'loadingAfr';
}

const initialState: AfrState = {
    afr: null,
    versionPropertiesChanged: null,
    status: "idle"
}

const createNullVersionState = function () {
    var nullWorkState: WorkState = {
        balanceSheetComplete: null,
        revenuesComplete: null,
        expendituresComplete: null,
        noBalanceSheet: null,
        noRevenues: null,
        noExpenditures: null,
        noExceptionsWaivers: null,
        noConstructionProjects: null,
        noBonds: null,
        governmentalFundsComplete:null,
        proprietaryFundsComplete:null,
        fiduciaryFundsComplete: null,
        componentUnitsFundsComplete: null,
        noGovernmentalFunds: null,
        noProprietaryFunds: null,
        noFiduciaryFunds: null,
        noComponentUnitsFunds: null
    };
    var nullReviewState: ReviewState = {
        generalInfoReviewed: null,
        balanceSheetReviewed: null,
        expendituresReviewed: null,
        revenuesReviewed: null,
        debtReviewed: null,
        componentUnitsReviewed: null,
        auditReviewed: null,
        certificationReviewed: null,
        supplementalReviewed: null,
        cr939CourtInnovationsReviewed: null,
        cr939LegalAidProgramReviewed: null,
        cr939LawLibraryReviewed: null,
        cr939JuvenileAlternativeProgramReviewed: null,
        cr939SummaryReviewed: null,
        cr29PartAReviewed: null,
        cr29PartBReviewed: null,
        cr29PartCReviewed: null,
        cr29PartDReviewed: null,
        cr29PartEReviewed: null,
        cr29CertificationReviewed: null,
        governmentalFundsReviewed: null,
        proprietaryFundsReviewed: null,
        fiduciaryFundsReviewed: null,
        componentUnitsFundsReviewed: null
    };
    var nullAdminState: any = {
        adminReconciled: null,
        jLACNonCompliance: null,
        versionStatus: null
    };
    var nullVersionState = {
        workState: nullWorkState,
        reviewState: nullReviewState,
        adminState: nullAdminState
    }
    return nullVersionState;
}

export const createDimensionReferenceFromInput = function (dri: DimensionReferenceInput): DimensionReference {
    var result: DimensionReference = {
        axis: {
            id: dri.axisId,
            name: "",
            code: "",
            documentation: "",
            references: [],
            membersByYear: [],
            typedDomain: null,
            domainDatatype: "string"
        },
        member: dri.memberId ? {
            id: dri.memberId,
            name: "",
            code: "",
            documentation: "",
            references: [],
            datatype: "string",
            labels: [],
            isAbstract: true,
            isCredit: false,
            isDeprecated: false,
            periodtype: "DURATION",
            domainDatatype: "",
            deprecation: null

        } : null,
        typedContent: null
    }
    return result;
}
export const getSliceMemberReference = function (tbl: Table, memberId: string): DimensionReference | null {
    var ref: DimensionReference | null = null;
    for (var tbe of tbl.nthlevelsliceHierarchy) {
        var dim = tbe.dimensionaspect;
        if (!dim) {
            continue;
        }
        if (dim.member !== null) {
            if (dim.member.code === memberId || dim.member.id === memberId || dim.member.name === memberId) {
                ref = {
                    axis: { ...dim.axis },
                    member: { ...dim.member },
                    typedContent: null
                }
            }
        } else {
            continue;
        }
    }
    return ref;
}

// NOTE - we use member name here rather than member id since the data is coming from the UI and the text will be the name
export const getMemberReference = function (a: Axis, memberName: string): DimensionReference | null {
    if (!a.membersByYear || a.membersByYear.length === 0) {
        return null;
    }
    var memCandidates = a.membersByYear.filter(m => (m.name === memberName || m.code === memberName || m.id === memberName));
    if (memCandidates.length === 0) {
        return null;
    }
    return {
        axis: { ...a }, member: memCandidates[0], typedContent: null
    }
}

export const getTypedMemberReference = function (a: Axis, dim: Concept, tvalue: string): DimensionReference | null {
    return {
        axis: { ...a }, member: dim, typedContent: tvalue
    }
}

export const getTable = function (afr: Afr | null, tblName: string) {
    if (!afr) {
        return null;
    }
    if (!afr.currentversion) {
        return null;
    }
    for (var tbl of afr.currentversion.tables) {
        if (tbl.structure.name === tblName) {
            return tbl;
        }
    }
    return null;
}

export const getColumnAxisId = function (ft: FactTable) {
    const ch = ft.structure.columnHierarchy;
    if (!ch || !ch.axes || ch.axes.length === 0) {
        // Now check to see if this is a concept column table
        if (ft.structure.nthlevelcolumnHierarchy[0].conceptaspect) {
            return "ConceptAxis";
        }
        return "period";
    }
    return ch.axes[0].id;
}

export const getDimensionForFact = function (f: Fact, axisId: string | null) {
    for (var dr of f.dimensions) {
        if (dr.axis.id === axisId) {
            return dr.member;
        }
    }
    return null;
}

export const getDimensionForRow = function (frow: FactTableRow, axisId: string) {
    for (var dr of frow.dimensions) {
        if (dr.axis.id === axisId) {
            return dr.member;
        }
    }
    return null;
}


export const getRowDimensionReferenceFromTable = function (tbl: FactTable, axisId: string, memberId: string) {
    if (!tbl.structure || !tbl.structure.rowHierarchy || !tbl.structure.rowHierarchy.axes) {
        return null;
    }
    var a: Axis | null = null;
    for (var nextAxis of tbl.structure.rowHierarchy.axes) {
        if (nextAxis.id === axisId) {
            a = nextAxis;
        }
    }
    if (!a) {
        return null;
    }
    for (var mem of a.membersByYear) {
        if (mem.id === memberId || (mem.code === memberId)) {
            var result: any = { axis: { ...a }, member: { ...mem }, typedContent: null };
            return result;
        }
    }
    return null;
}
const findMatchingDimensionMember = function (entries: TableBreakdownEntry[], a: Axis, dimid: string): DimensionReference | null {
    for (var entry of entries) {
        if (entry.dimensionaspect && entry.dimensionaspect.member) {
            if (entry.dimensionaspect.member.id === dimid) {
                return { ...entry.dimensionaspect, axis: a }
            }
        }
        if (entry.sublevels && entry.sublevels.length > 0) {
            var dr: DimensionReference | null = findMatchingDimensionMember(entry.sublevels, a, dimid);
            if (dr) {
                return dr;
            }
        }
    }
    return null;
}
export const getColumnDimensionReferenceFromTable = function (tbl: FactTable, axisId: string, memberId: string) {
    if (!tbl.structure || !tbl.structure.nthlevelcolumnHierarchy || !tbl.structure.columnHierarchy) {
        return null;
    }
    var a: Axis | null = null;
    for (var nextAxis of tbl.structure.columnHierarchy.axes) {
        if (nextAxis.id === axisId) {
            a = nextAxis;
        }
    }
    if (!a) {
        return null;
    }
    return findMatchingDimensionMember(tbl.structure.nthlevelcolumnHierarchy, a, memberId);
}


export const getColumnConceptsFromTable = function (tbl: FactTable) {
    if (!tbl.structure || !tbl.structure.nthlevelcolumnHierarchy) {
        return null;
    }
    var result:any = [];
    for (var firstLevel of tbl.structure.nthlevelcolumnHierarchy) {
        if (firstLevel.sublevels) {
            for (var secondLevel of firstLevel.sublevels) {
                if (secondLevel.conceptaspect) {
                    result.push(secondLevel.conceptaspect);
                }
            }
        }
    }
    return result;
}

export const getRowConceptFromTable = function (tbl: FactTable, rowCode: string) {
    if (!tbl) {
        return null;
    }
    if (!tbl.structure || !tbl.structure.nthlevelrowHierarchy) {
        return null;
    }

    for (var firstLevel of tbl.structure.nthlevelrowHierarchy) {
        if (firstLevel.conceptaspect && firstLevel.conceptaspect.code === rowCode) {
            return firstLevel.conceptaspect;
        }
    }
    return null;
}

export const getRowIdForAccountAndDimensions = function (account: Account | null, dimensions: Array<DimensionReference>) {
    var rowId = account ? account.code : "";
    if (dimensions && dimensions.length > 0) {
        for (var dr of dimensions) {
            if (dr.member != null) {
                rowId += "|";
                if (dr.axis.typedDomain) {
                    rowId += dr.typedContent ? dr.typedContent : "";
                } else {
                    rowId += dr.member.code;
                }
            }
        }
    }
    return rowId;
}

const getRowId = function (tblRow: FactTableRow) {
    if (!tblRow.account) {
        // This is a flipped table - use the first dimension code
        var rowDimId = "";
        if (tblRow.dimensions && tblRow.dimensions.length > 0) {
            for (var d of tblRow.dimensions) {
                if (d.member != null) {
                    rowDimId += "|";
                    if (d.member.name && d.member.name.indexOf(".domain") > 0) {
                        var tcontent = d.typedContent ? d.typedContent : "";
                        if (tcontent.length > 0) {
                            rowDimId += tcontent;
                        } else {
                            rowDimId += "?";
                        }
                    } else {
                        rowDimId += d.member.code;
                    }
                }
            }
        }
        return rowDimId;
    }
    var rowId = tblRow.account.code;
    if (tblRow.dimensions && tblRow.dimensions.length > 0) {
        for (var dr of tblRow.dimensions) {
            if (dr.member != null) {
                rowId += "|";
                rowId += dr.member.code;
            }
        }
    }
    return rowId;
}
export const getRow = function (table: FactTable, rowId: string) {
    for (var row of table.rows) {
        var tblRowId = getRowId(row);
        if (tblRowId === rowId) {
            return row;
        }
        // The rowid passed in might NOT contain a leading pipe character, while the generated row id will contain a pipe character beginning a dimensional sequence.
        if (tblRowId.substring(1) === rowId) {
            return row;
        }
    }
    return null;
}
const getKeyValue = <U extends keyof T, T extends object>(key: U) => (obj: T) =>
    obj[key];

export const getSelectedAffiliateReport = function (currentAfr: Afr | null, affiliateCode: string) {
    if (currentAfr && currentAfr.affiliateReporting && currentAfr.affiliateReporting.length > 0) {
        var affiliatedEntity = currentAfr.affiliateReporting.find(x => x.affiliateReference && (x.affiliateReference.code === affiliateCode));
        if ((typeof affiliatedEntity) === "undefined") return null;
        return affiliatedEntity;
    }
    return null;
}
export const generateAffiliateNoDataState = function (currentAfr: Afr | null, affiliate: string, nodataName: string) {
    var newLocalState = {} as any;
    newLocalState[nodataName] = false;
    if (currentAfr) {
        var tbl: FactTable | null = getTable(currentAfr, enums.tableName.affiliateNodata);
        if (tbl) {
            var criteria: any = {
                ReportingUnitAxis: affiliate,
                AfrNoDataAxis: enums.nodataAreaName[nodataName]
            }
            var matchFact = getMatchingFact(tbl, "AFRND_NoData", criteria);
            // NOTE - We convert boolean facts into a 0 (false) or 1 (true)
            if (matchFact && matchFact.value !== 0) {
                newLocalState[nodataName] = true;
            }
        }
    }
    return newLocalState;
}

export const generateLocalStateFromEventProperties = function (currentAfr: Afr | null, events: Array<string>) {
    // get current version state
    var newLocalState = {} as any;
    if (currentAfr && currentAfr.currentversion) {
        var currentVersionState = currentAfr.currentversion.versionState as any;
        var eventFirstLevel: string = ""
        var eventSecondLevel: string = ""
        for (let e in events) {
            if (typeof events[e] === 'undefined') {
                console.log("undefined event")
                continue;
            }
            var eventParts = events[e].split("_");
            if (eventParts.length < 2) {
//                console.log("invalid event", eventParts);
                continue;
            }
            eventFirstLevel = eventParts[0].toString(); // workState, reviewState, adminState
            eventSecondLevel = eventParts[1].toString(); // balanceSheetComplete, revenuesComplete, expendituresComplete, noBalanceSheet, noRevenues, noExpenditures, noExceptionsWaivers..etc
            var vEntry = currentVersionState[eventFirstLevel][eventSecondLevel];
            newLocalState[eventFirstLevel + "_" + eventSecondLevel] = vEntry;

        }
    }
    return newLocalState;
}

export const getAdminEventProperty = function (currentAfr: Afr | null, propName: string) {
    // get current version state
    var propValue = false;
    if (currentAfr && currentAfr.currentversion) {
        var currentVersionState = currentAfr.currentversion.versionState as any;
        if (currentVersionState.hasOwnProperty("adminState")) {
            var adminState = currentVersionState.adminState;
            if (adminState.hasOwnProperty(propName)) {
                propValue = adminState[propName];
            }
        }
    }
    return propValue;
}
export const existFactsByFundGroup = function (currentAfr: Afr | null, tableNames: Array<string>, fundGroup: string) {
    if (!currentAfr) {
        return false;
    }
    var table: FactTable | null;
    var columnMatches: string = "";
    // get each table in the list
    for (let e in tableNames) {
        table = getTable(currentAfr, tableNames[e]);
        if (table) {
            if (columnMatches.length === 0) {
                for (var tbeGroup of table.structure.nthlevelcolumnHierarchy) {
                    if (fundGroup && tbeGroup.label !== fundGroup) {
                        continue;
                    }
                    var t: string | null = null;
                    var tGroup = "";
                    var colGroup: any[] = [];
                    if (tbeGroup.sublevels) {
                        for (var tbeCol of tbeGroup.sublevels) {
                            columnMatches += "|" + tbeCol.label;
                        }
                    } else {
                        columnMatches += "|" + tbeGroup.label;
                    }
                }
            }
            // look for fact under this table in redux
            for (var row of table.rows) {
                for (var fact of row.facts) {
                    for (var dim of fact.dimensions) {
                        if (dim.axis && dim.axis.name === "Fund groups") {
                            if (dim.member && dim.member.name) {
                                var parts: string[] = dim.member.name.split(" - ");
                                var dimKey = dim.member.name;
                                if (parts.length > 1) {
                                    dimKey = parts[1].replace(" Funds", "").replace(" Fund", "");
                                }
                                if (columnMatches.indexOf(dimKey) >= 0) {
                                    return true;
                                }

                            }
                        }
                    }
                }
            }
        }
    }
    return false;

}

export const existFactsInTable = function (currentAfr: Afr, tableNames: Array<string>) {
    var table: FactTable | null;

    // get each table in the list
    for (let e in tableNames) {
        table = getTable(currentAfr, tableNames[e]);

        if (!table) { return false }

        // look for fact under this table in redux
        var newFactArray = table.rows.filter(function (rows) {
            return rows.facts.length > 0
        });

        if (newFactArray.length > 0) {
            return true;
        }
    }
    return false;

}

export const existAffiliateFactsInTable = function (currentAfr: Afr, affiliateCode : string, tableNames: Array<string>) {
    var table: FactTable | null;

    // get each table in the list
    for (let e in tableNames) {
        table = getTable(currentAfr, tableNames[e]);

        if (!table) { return false }

        // look for fact under this table in redux
        var newFactArray = table.rows.filter(function (row) {
            if (row.facts.length === 0) {
                return false;
            }
            var hasAffiliate = false;
            row.dimensions.map((dim) => {
                if (dim.member && dim.member.code === affiliateCode) {
                    return true;
                }
                return false;
            });
        });

        if (newFactArray.length > 0) {
            return true;
        }
    }
    return false;
}



const getSiblingRow = function (table: FactTable, rowId: string) {
    const sibling = rowId.split("|")[0];
    const isSubAccount = (sibling.length != rowId.replace("|", "").length);
    let index: number = -1;

    for (var row of table.rows) {
        var tblRowId = getRowId(row);
        index = index + 1;

        if (!isSubAccount) {
            // this line returns a sibling of a main account ( no subaccount )
            return { row: row, index: index };
        }
        if (tblRowId.includes(sibling)) {
            return { row: row, index: index };
        }
    }
    // this code handle the scenario when we are adding a new account with subrows
    if (isSubAccount) {
        for (var row of table.rows) {
            var tblRowId = getRowId(row);
            return { row: row, index: 0 };
        }
    }
    return { row: null, index: null };
}


export const getFact = function (row: FactTableRow, fieldName: string) {
    if (fieldName === "period") {
        if (row.facts.length > 0) {
            return row.facts[0];
        }
        return null;
    }
    var tableFlipped: boolean = (row.account ? false : true);
    for (var fact of row.facts) {

        var columnValue = (tableFlipped ? fact.concept.id : fact.dimensions[0].member !== null ? fact.dimensions[0].member.id : "");
        if (columnValue === fieldName) {
            return fact;
        }
    }
    return null;
}
export const factHasAspect = function(fact: Fact, aspectValue: string) {
    for (var dr of fact.dimensions) {
        if (!dr || !dr.member) {
            continue;
        }

        /* The line below as previously written was not working as intended.  The aspect value represents codes for fund groups.  Take
           the aspect value for Govermental fund group i.e. "001,100,200,300". The facts for expenditures could contain
           an object code of 10 (fund groups and object codes are both dimensions on facts).  An dimensional value of 10 would return an index of 0 or greater
           for an aspect value of "001,100,200,300".  I have corrected the issue, the note here is because there could be other areas
           where index of is being used instead of includes.*/
        if (aspectValue.split(",")
            .filter(Boolean)
            .includes(dr.member.code)) {
            return true;
        }
    }
    return false;
}
export const getMatchingFact = function (table: FactTable, conceptId: string, dimensionCriteria: any) {
    for (var row of table.rows) {
        for (var fact of row.facts) {
            if (fact.concept.id !== conceptId) {
                continue;
            }
            if (!dimensionCriteria) {
                return fact;
            }
            if (!fact.dimensions) {
                continue;
            }
            var dimMatchCount = 0;
            for (var dr of fact.dimensions) {
                if (!dr) {
                    continue;
                }
                if (!dimensionCriteria.hasOwnProperty(dr.axis.id)) {
                    continue;
                }
                var keyToMatch = dimensionCriteria[dr.axis.id];
                if (dr.member != null) {
                    if (dr.member.id === keyToMatch) {
                        dimMatchCount++;
                    } else if (dr.member.code === keyToMatch) {
                        dimMatchCount++;
                    }
                } else {
                    continue;
                }
            }
            if (dimMatchCount === Object.keys(dimensionCriteria).length) {
                return fact;
            }
        }
    }
    return null;
}

const getNoDataTable = function (currentAfr: Afr | null) {
    if (!currentAfr) {
        return null;
    }
    var tbl = getTable(currentAfr, "uas_001b_afr-no-data-declarations");
    if (!tbl) {
        return null;
    }
    return tbl;
}
const getNoDataFact = function (currentAfr: Afr | null, category : string ) : Fact | null {
    var tbl = getNoDataTable(currentAfr);
    if (!tbl) {
        return null;
    }
    var criteria: any = {
        AfrNoDataAxis: enums.nodataAreaName[category]
    }

    var f: Fact | null = getMatchingFact(tbl, "AFRND_NoData", criteria);
    if (f) {
        return f;
    }
    return null;
}
export const hasNoDataFact = function (currentAfr: Afr | null, category: string): boolean {

    var f: Fact | null = getNoDataFact(currentAfr,category);
    if (f) {
        return true;
    }
    return false;
}

export const createNoDataFact = function (currentAfr: Afr, category: string): Fact | null {
    if (!currentAfr) {
        return null;
    }
    var f : Fact | null = getNoDataFact(currentAfr, category);
    if (f) {
        return null;
    }
    var tbl = getTable(currentAfr, "uas_001b_afr-no-data-declarations");
    if (!tbl) {
        return null;
    }
    var memberId = enums.nodataAreaName[category];
    var dr = getColumnDimensionReferenceFromTable(tbl, "AfrNoDataAxis", memberId);
    if (!dr) {
        return null;
    }

    var ndFact: Fact = {
        value: 1,
        textValue: null,
        concept: {
            id: "AFRND_NoData"
        },
        dimensions: [dr],
        validationerrors: [],
        accountCode: "AFRND_NoData"
    }
    return ndFact;
}

const getAuditPdf = function (audit: Audit | null): DocumentRelationship | null {
    if (audit && audit.auditPdf && !utilityHelper.isNullDocumentRelationship(audit.auditPdf)) {
        return audit.auditPdf;
    }
    return null;
}

const createDocEntry = function (doc: DocumentRelationship, affiliateNameMap : any ): DocumentListEntry {
    var name: string = "";
    let docrole = doc.role;
    var labelMap: any = enums.documentRoleLabel;
    if (labelMap.hasOwnProperty(doc.role)) {
        docrole = labelMap[doc.role];
    }
    switch (doc.role) {
        case enums.documentRole.impact_fee_exception:
            name = "Impact Fee Exceptions PDF";
            break;
        case enums.documentRole.impact_fee_waiver:
            name = "Impact Fee Waivers PDF";
            break;
        default:
            if (doc.affiliate && affiliateNameMap.hasOwnProperty(doc.affiliate)) {
                name = affiliateNameMap[doc.affiliate];
            } else {
                name = docrole;
            }
            break;
    }
    return { document: doc, type: docrole, name: name };
}
export const getDocumentList = function (currentAfr: Afr) {
    // get all the documents linked to a AFR
    var list: DocumentListEntry[] = [];
    var ownlist: DocumentListEntry[] = [];
    var affiliatelist: DocumentListEntry[] = [];
    var supplementalList: DocumentListEntry[] = [];
    var roleDocs: DocumentRelationship[] = [];
    if (currentAfr && currentAfr.currentversion && currentAfr && currentAfr.currentversion.documents) {
        roleDocs = currentAfr.currentversion.documents;
    }
    var affiliateNameMap: any = {};
    //get the documents linked to current AFR
    if (currentAfr && currentAfr.currentversion) {
        // pulling audit PDF
        var auditPdf = getAuditPdf(currentAfr.currentversion.audit);
        if (auditPdf) {
            var auditType = currentAfr.currentversion.audit.auditType ? currentAfr.currentversion.audit.auditType : "NA";

            ownlist.push({ document: auditPdf, type: auditType, name: "Audit" });
        }
    }
    //get Documents linked to affiliated entities
    if (currentAfr && currentAfr.affiliateReporting) {

        for (var ae of currentAfr.affiliateReporting) {
            if (!ae.affiliateReference) {
                // TODO - we have a problem
                continue;
            }
            affiliateNameMap[ae.affiliateReference.code] = ae.affiliateReference.name;
            var affAuditPdf = getAuditPdf(ae.audit);
            if (affAuditPdf) {
                var affAuditDoc = { ...affAuditPdf, affiliate: ae.affiliateReference.code };
                affiliatelist.push({ document: affAuditDoc, type: affAuditPdf.role, name: ae.affiliateReference.name });
            }
        }
        affiliatelist.sort((a, b) => a.name.localeCompare(b.name));
    }
    // get supplementary documents
    if (roleDocs.length > 0) {
        for (var doc of currentAfr.currentversion.documents) {
            if (utilityHelper.isNullDocumentRelationship(doc)) {
                continue;
            }
            if (!doc.documentId) {
                continue;
            }
            if (doc.affiliate) {
                supplementalList.push(createDocEntry(doc, affiliateNameMap));
            } else {
                ownlist.push(createDocEntry(doc, affiliateNameMap));
            }
        }
        supplementalList.sort((a, b) => (a.name + a.type).localeCompare((b.name + b.type)));
    }
    ownlist.map(x => list.push(x));
    affiliatelist.map(x => list.push(x));
    supplementalList.map(x => list.push(x));

    return list;
    /*TODO: continue gathering the list of documents  */
}

export const getEventList = function (currentAfr: Afr | null) {
    // get all the documents linked to a AFR

    var list: Array<EventGeneric> = [];
    var status: string;

    //get status event list
    if (currentAfr && currentAfr.currentversion && currentAfr.currentversion.statusEvents) {
        var event = currentAfr.currentversion.statusEvents;

        // pull the event information
        var lastEventSignature: string = "";
        var rank : number = 0;
        for (var e of event) {
            status = "status";
            if (e.statevalue.includes("version:")) status = "version";
            var displayDetail = utilityHelper.toTitleCase(e.statevalue.replace("Re-Cert", "ReCert"))
                .replace("Recert", "Re-Cert")
                .replace(" Dfs", " DFS")
                .replace(" By ", " by ");
                                                                                                                                                           
            var eventUserName = utilityHelper.getRoleUserName(e.creator);
            var eventSignature: string = eventUserName + "_" + displayDetail + "_" + e.timestamp;
            if (eventSignature !== lastEventSignature) {
                lastEventSignature = eventSignature;
                // The rank is appended to the timestamp so otherwise identical timestamps will be sorted by order. Since this value is appended to the end of the millisecond part of timestamp there's
                // no reall chance of a timestamp with a rank appended inadvertantly matching a timestamp for an unrelated event.
                rank = rank + 1;
                list.push({ user: eventUserName, type: status, date: e.timestamp + rank, detail: displayDetail });
            }
        }
    }

    // get comments history

    return list;
    /*TODO: continue gathering the list of events from other sources  */

}

export const getEventByName = function (currentAfr: Afr | null, eventName: string): EventGeneric | null {
    // get the event listall the documents linked to a AFR
    var list: Array<EventGeneric> = getEventList(currentAfr);
    var event: EventGeneric;
    // todo: sort the list by date desc
    list.sort((a, b) => (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0));

    // filter by name the event list
    var filteredList = list.filter(x => x.detail.toLowerCase().includes(eventName.toLowerCase()));

    if (filteredList.length > 0) {
        // return the newer event
        event = filteredList[0];
        return event;
    }

    return null;

}

export const isCRA = function (currentAffiliatedEntity: any) {
    return ((currentAffiliatedEntity && currentAffiliatedEntity.affiliateReference && currentAffiliatedEntity.affiliateReference.category === CRA) ? true : false);
}

/*
 * There are three kinds of tables where we need to match an existing fact for removal
 * 1. Regular account rows by fund columns (e.g. revenue, expenditures, balance sheet, DEW)
 * 2. "Scalar" tables where there is only one column for period
 * 3. "Flipped" tables where the dimensions are in the rows, and the columns are concepts (e.g. affiliate-totals)
 * 
 * We also supply the axis id, in order to be sure to fetch the correct dimension from the fact to ensure correct matching.
 */

const matchFactByFund = function (fact: Fact, axisId: string, columnIdentifier: string) {
    var factColumnId = "";
    if (axisId === "ConceptAxis") {
        // This is a concept=column table, so 
        factColumnId = fact.concept.id;
    } else {
        var factMatchMembers = fact.dimensions.filter(dimref => dimref.axis.id === axisId);
        if (!factMatchMembers || factMatchMembers.length === 0) {
            return false;
        }
        if (factMatchMembers[0].member !== null) {
            factColumnId = factMatchMembers[0].member.id;
        }

    }
    if (factColumnId === columnIdentifier) {
        return true;
    }
    return false;
}

const getColumnDimension = function (table: FactTable, fundGroup: string) {
    var struc = table.structure;
    if (struc.columnHierarchy && struc.columnHierarchy.axes != null && struc.columnHierarchy.axes.length > 0) {
        const colAxis = struc.columnHierarchy.axes[0];
        for (var member of colAxis.membersByYear) {
            if (member.id === fundGroup) {
                return member;
            }
        }
    }
    return null;
}

const findColumnDimensionReference = function (tbeList: Array<TableBreakdownEntry>, fundGroup: string, colAxis: any): DimensionReference | null {
    for (var tbe of tbeList) {
        if (tbe.sublevels && tbe.sublevels.length > 0) {
            var found = findColumnDimensionReference(tbe.sublevels, fundGroup, colAxis);
            if (found) {
                return found;
            }
        } else if (tbe.dimensionaspect && tbe.dimensionaspect.member && tbe.dimensionaspect.member.id === fundGroup) {
            // Note - we don't retrieve the axis when we fetch the hierarchy - so was have to supply it in the call
            return {
                axis: colAxis,
                member: { ...tbe.dimensionaspect.member },
                typedContent: null
            };
        }
    }
    return null;
}

const getColumnDimensionReference = function (table: FactTable, fundGroup: string) {
    var struc = table.structure;
    var colAxis: any = null;
    if (struc.columnHierarchy && struc.columnHierarchy.axes != null && struc.columnHierarchy.axes.length > 0) {
        const tableBreakdown: TableBreakdownEntry = struc.columnHierarchy;
        colAxis = tableBreakdown.axes[0];
        if (colAxis.membersByYear.length > 0) {
            for (var member of colAxis.membersByYear) {
                if (member.id === fundGroup) {
                    return { axis: colAxis, member: member, typedContent: null };
                }
            }
        } else {
            // The axis didn't have a calculated membership, so check the breakdown structure
            var firstRef = tableBreakdown.firstDimension;
            if (firstRef && firstRef.member !== null && firstRef.member.id === fundGroup) {
                return { ...firstRef };
            }
        }
    }
    // We didn't find a match in the main column hierarchy - check the nth level hierarchy
    if (colAxis && struc.nthlevelcolumnHierarchy) {
        return findColumnDimensionReference(struc.nthlevelcolumnHierarchy, fundGroup, colAxis);
    }

    return null;
}

const addFacts = function (currentAfr: Afr, currentVersion: AfrVersionInput) {
    if (!currentAfr.currentversion.tables) {
        alert("no tables");
        return;
    }
    for (var ft of currentAfr.currentversion.tables) {
        for (var row of ft.rows) {
            var rowcid = row.account ? row.account.id : "";
            for (var fact of row.facts) {
                var cid = rowcid;
                if (fact.concept && fact.concept.id !== cid) {
                    cid = fact.concept.id;
                }
                var dims:any = [];
                if (fact.dimensions && fact.dimensions.length > 0) {
                    for (var dr of fact.dimensions) {
                        if (dr.member != null) {
                            dims.push({
                                axisId: dr.axis.id,
                                memberId: dr.member.id,
                                typedContent: dr.typedContent
                            });
                        }
                    }
                }
                if (!cid || cid.length === 0) {
                    continue;
                }
                var saveFact = {
                    conceptId: cid,
                    decimalValue: fact.value,
                    dimensions: dims
                }
                currentVersion.facts.push(saveFact);
            }
        }
    }
}
const getCreatorId = function (u: logerUser): string {
    if (!u) {
        return "";
    }
    if (u.id) {
        return u.id + "";
    }
    if (u.login) {
        return u.login;
    }
    return "";
}
export const isFundGroupOriented = function (afr: Afr | null): boolean {
    if (!afr) {
        return false;
    }
    if (afr.taxonomyType !== TaxonomyTypeEnum.AFR) {
        return false;
    }
    if (afr.year < 2024) {
        return false;
    }
    return true;
}
const addEvents = function (currentAfr: Afr, currentVersion: AfrVersionInput) {
    for (var event of currentAfr.currentversion.events) {
        var newEvent = {
            creatorId: getCreatorId(event.creator),
            timestamp: event.timestamp,
            payload: "",
            state: event.state,
            statevalue: event.statevalue
        }
        currentVersion.stateChangeEvents.push(newEvent);
    }
    for (var sevent of currentAfr.currentversion.statusEvents) {
        var newSEvent = {
            creatorId: getCreatorId(sevent.creator),
            timestamp: sevent.timestamp,
            payload: "",
            statevalue: sevent.statevalue
        }
        currentVersion.statusChangeEvents.push(newSEvent);
    }
}

export const getFirstDocumentIndexFromRole = function (currentAfr: Afr, role: string, affiliate: string | null) {
    if (role.indexOf(' ') > 0) {
        // We have the public role, convert to the enum version
        role = role.toUpperCase().split(' ').join("_");
    }
    var index = -1;
    if (currentAfr && currentAfr.currentversion && currentAfr.currentversion.documents && currentAfr.currentversion.documents.length > 0) {
        if (affiliate) {
            index = currentAfr.currentversion.documents.findIndex(x => x.role === role && x.affiliate && x.affiliate === affiliate);
        } else {
            index = currentAfr.currentversion.documents.findIndex(x => x.role === role && !x.affiliate);
        }
    }
    return index;
}

const addDocuments = function (currentAfr: Afr, currentVersion: AfrVersionInput) {
    var srcVersion = currentAfr.currentversion;
    if (!srcVersion.documents || srcVersion.documents.length === 0) {
        return;
    }
    for (var vdoc of srcVersion.documents) {
        var newdoc: DocumentRelationshipInput =
        {
            ...vdoc
        };
        currentVersion.documents.push(newdoc);
    }
}

const addAudit = function (currentAfr: Afr, currentVersion: AfrVersionInput) {
    var srcVersion = currentAfr.currentversion;
    if (!srcVersion) {
        return;
    }
    var audit = srcVersion.audit;
    if (!audit) {
        return;
    }
    var pdf: DocumentRelationshipInput | null = null;
    if (audit.auditPdf && audit.auditPdf.documentId) {
        pdf = {
            ...audit.auditPdf,
        };
    }
    var saveAudit: AuditInput = {
        datePerformed: audit.datePerformed,
        auditorId: audit.auditorReference ? audit.auditorReference.code : null,
        auditPdf: pdf,
        auditType: audit.auditType,
        sendAuditPdf: audit.sendAuditPdf
    };
    currentVersion.audit = saveAudit;
}
const createCertifier = function (c: Certifier | null) : any | null {
    if (!c) {
        return null;
    }
    var cInfo: any | null = null;
    if (c.contactInfo && c.contactInfo.number) {
        cInfo = {
            phoneNumber: c.contactInfo.number.number,
            extension: c.contactInfo.number.extension,
            email: c.contactInfo.email
        };
    }
    var certifier : any = {
        title: c.title,
        name: c.name,
        agree: c.agree,
        contactInfo: cInfo
    }
    return certifier;

}
const addCertification = function (currentAfr: Afr, currentVersion: AfrVersionInput) {
    var srcVersion = currentAfr.currentversion;
    if (!srcVersion) {
        return;
    }
    var cert = srcVersion.certification;
    if (!cert) {
        return;
    }

    var saveCert: any = {
        financialEmergency: cert.financialEmergency ? cert.financialEmergency : false,
        compliance: cert.compliance218,
        financialDeterioration: cert.financialDeterioration,
        submittedDocumentation: "",
        icertify: cert.icertify ? cert.icertify : false,
        first: createCertifier(cert.firstCertifier),
        second: createCertifier(cert.secondCertifier),
    };
    currentVersion.certification = saveCert;
}
const addAffiliateReporting = function (currentAfr: Afr, saveAfr: AfrInput) {
    var srcReporting = currentAfr.affiliateReporting;
    if (!srcReporting || srcReporting.length === 0) {
        return;
    }
    var saveReporting: Array<AffiliateReportingInput> = [];
    for (var ar of srcReporting) {
        if (!ar.affiliateReference) {
            // TODO - We have a problem
            continue;
        }
        var auditInput: AuditInput | null = null;
        if (ar.audit) {
            var pdf: DocumentRelationshipInput | null = null;
            if (ar.audit.auditPdf && ar.audit.auditPdf.documentId) {
                pdf = { ...ar.audit.auditPdf };
            }
            if (ar.audit.auditorReference) {
                auditInput = {
                    datePerformed: ar.audit.datePerformed,
                    auditorId: ar.audit.auditorReference.code,
                    auditPdf: pdf,
                    auditType: ar.audit.auditType,
                    sendAuditPdf: ar.audit.sendAuditPdf
                }
            } else {
                auditInput = {
                    datePerformed: ar.audit.datePerformed,
                    auditorId: null,
                    auditPdf: null,
                    auditType: ar.audit.auditType,
                    sendAuditPdf: null
                }

            }
        }
        var arInput: AffiliateReportingInput = {
            affiliateCode: ar.affiliateReference.code,
            reportingType: ar.reportingType,
            auditReceivedDate: ar.auditReceivedDate,
            audit: auditInput
        }
        saveReporting.push(arInput);
    }
    saveAfr.affiliateReporting = saveReporting;
}
const getAfrVersionToCommit = function (currentAfr: Afr, changed: versionState | null, submit: boolean): AfrInput {
    var cstate: versionState = changed ? changed : createNullVersionState();
    var commit: any = {
        afrId: "na",
        year: 0,
        affiliateReporting: [],
        auditReceivedDate: null,
        requestDate: null,
        version: {
            versionNumber: currentAfr.currentversion.versionNumber,
            amendmentNumber: currentAfr.currentversion.amendmentNumber,
            revisionNumber: currentAfr.currentversion.revisionNumber,
            audit: null,
            certification: null,
            facts: [],
            stateChangeEvents: [],
            statusChangeEvents: [],
            changedVersionStates: cstate,
            documents: [],
            withholdingStartDates: currentAfr.currentversion.withholdingStartDates,
            beginEnforcementLetterIssueDate: currentAfr.currentversion.beginEnforcementLetterIssueDate
        },
        submit: submit
    };
    if (!currentAfr || !currentAfr.currentversion) {
        return commit;
    }

    // The afrid scalar format used a comma between the entity and year. This should be reconciled
    commit.afrId = currentAfr.afrId;
    commit.year = currentAfr.year;
    // Now add the dates if we have any
    if (currentAfr.auditReceivedDate && currentAfr.auditReceivedDate !== "*") {
        // We have an explicit override
        commit.auditReceivedDate = currentAfr.auditReceivedDate;
    }
    if (currentAfr.requestDate) {
        commit.requestDate = currentAfr.requestDate;
    }
    addFacts(currentAfr, commit.version);
    addEvents(currentAfr, commit.version);
    addDocuments(currentAfr, commit.version);
    addAudit(currentAfr, commit.version);
    addCertification(currentAfr, commit.version);
    addAffiliateReporting(currentAfr, commit);
    return commit;
}
const getAffiliateReport = function (afr: Afr, affiliateId: string) {
    if (!afr || !afr.affiliateReporting || afr.affiliateReporting.length === 0) {
        return null;
    }
    for (var rep of afr.affiliateReporting) {
        if (rep.affiliateReference && (rep.affiliateReference.code === affiliateId)) {
            return rep;
        }
    }
    return null;
}
const getAffiliateReportIndex = function (afr: Afr, affiliateId: string) {
    if (!afr || !afr.affiliateReporting || afr.affiliateReporting.length === 0) {
        return -1;
    }
    var idx = 0;
    for (var rep of afr.affiliateReporting) {
        if (rep.affiliateReference && (rep.affiliateReference.code === affiliateId)) {
            return idx;
        }
        idx++;
    }
    return -1;
}
export const getTableTotals = function (afr: Afr, tableName: string) {
    var total = 0;
    var table = getTable(afr, tableName);
    if (table) {
        for (var row of table.rows) {
            for (var fact of row.facts) {
                total = total + utilityHelper.getNumber(fact.value);
            }
        }
    }

    return total;
}

export const getMatchingFacts = function (currentAfr: Afr, matchFacts: MatchTableFacts) : FactTableRow[] {
    var matchRows: FactTableRow[] = [];
    const tblName = matchFacts.tableName;
    if (!currentAfr.currentversion) {
        alert("no current version");
        return matchRows;
    }
    const tbl = getTable(currentAfr, tblName);
    if (tbl) {
        const matchId = utilityHelper.getRowSignature(matchFacts.matchConcept.conceptId, matchFacts.matchConcept.dimensions);
        if (matchId.indexOf("|") < 0) {
            // There are no dimension checks
            tbl.rows.map(ftr => utilityHelper.getRowSignature(ftr.account != null ? ftr.account.id : "", null) === matchId ? matchRows.push({ ...ftr }) : null);
        } else {
            tbl.rows.map(ftr => utilityHelper.getRowSignature(ftr.account != null ? ftr.account.id : "", ftr.dimensions) === matchId ? matchRows.push({ ...ftr }) : null);
        }
    }
    return matchRows;
}

export const hasDiscreteAffiliates = function (currentAfr: Afr | null) : boolean {
    if (!currentAfr) {
        return false;
    }
    if (!currentAfr.affiliateReporting || currentAfr.affiliateReporting.length == 0) {
        return false;
    }
    for (var arep of currentAfr.affiliateReporting) {
        if (!arep.reportingType) {
            // An unknown reporting type is treated as possibly discrete
            return true;
        }
        switch (arep.reportingType) {
            case enums.affiliatedEntityReportingType.discrete:
            case enums.affiliatedEntityReportingType.notSpecified:
                return true;
        }
    }
    return false;
}

const allowsDuplicateAccounts = (acctTable: FactTable): boolean => {
    if (!acctTable) {
        return false;
    }
    if (!acctTable.structure.rowHierarchy.axes) {
        return false;
    }
    for (var axis of acctTable.structure.rowHierarchy.axes) {
        if (axis.typedDomain) {
            return true;
        }
    }
    return false;
}

export const loadAfr = createAsyncThunk(
    "Afr/GetAfrDetails",
    async (afrId: string): Promise<Afr | null> => {
        try {
            const result = await http<undefined, Afr>({
                path: `/afr/${afrId}`,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export interface AfrCommitCall {
    currentAfr: Afr,
    changedVersionStates: versionState | null,
    submit: boolean
}
export const commitAfr = createAsyncThunk(
    "Afr/CommitAfr",
    async (afrCommit: AfrCommitCall): Promise<Afr | null> => {
        try {
            var commitAfr: AfrInput = getAfrVersionToCommit(afrCommit.currentAfr,
                afrCommit.changedVersionStates,
                afrCommit.submit
            );
            var afrIdParts = afrCommit.currentAfr.afrId.split("^");
            var ecode = afrIdParts[0];
            var commitRequest: CommitAfrRequest = {
                entityId: ecode,
                afrYear: afrCommit.currentAfr.year,
                afrToSave: commitAfr
            }
            const result = await http<CommitAfrRequest, Afr>({
                path: `/afr/commitAfr`,
                method: 'post',
                body: commitRequest
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            console.log(JSON.stringify(ex));
            return null;
        }
    });

export const createAmendment = createAsyncThunk(
    "Afr/CreateAmendment",
    async (afrCommit: AfrCommitCall): Promise<Afr | null> => {
        try {
//            var afrIdParts = afrCommit.currentAfr.afrId.split("_");
            var ecode = afrCommit.currentAfr.afrId;
            var amendRequest: CommitAfrRequest = {
                entityId: ecode,
                afrYear: afrCommit.currentAfr.year,
                afrToSave: null
            }
            const result = await http<CommitAfrRequest, Afr>({
                path: `/afr/createAmendment`,
                method: 'post',
                body: amendRequest
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            console.log(JSON.stringify(ex));
            return null;
        }
    });

export const deleteCurrentVersion = createAsyncThunk(
    "Afr/DeleteCurrentVersion",
    async (afrCommit: AfrCommitCall): Promise<Afr | null> => {
        try {
//            var afrIdParts = afrCommit.currentAfr.afrId.split("_");
            var ecode = afrCommit.currentAfr.afrId;
            var deleteRequest: CommitAfrRequest = {
                entityId: ecode,
                afrYear: afrCommit.currentAfr.year,
                afrToSave: null
            }
            const result = await http<CommitAfrRequest, Afr>({
                path: `/afr/deleteCurrentVersion`,
                method: 'post',
                body: deleteRequest
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            console.log(JSON.stringify(ex));
            return null;
        }
    });

export const returnByDFS = createAsyncThunk(
    "Afr/ReturnByDFS",
    async (afrCommit: AfrCommitCall): Promise<Afr | null> => {
        try {
//            var afrIdParts = afrCommit.currentAfr.afrId.split("_");
            var ecode = afrCommit.currentAfr.afrId;
            var dfsReturnRequest: CommitAfrRequest = {
                entityId: ecode,
                afrYear: afrCommit.currentAfr.year,
                afrToSave: null
            }
            const result = await http<CommitAfrRequest, Afr>({
                path: `/afr/returnByDFS`,
                method: 'post',
                body: dfsReturnRequest
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            console.log(JSON.stringify(ex));
            return null;
        }
    });

export const verifiedByDFS = createAsyncThunk(
    "Afr/VerifiedByDFS",
    async (afrCommit: AfrCommitCall): Promise<Afr | null> => {
        try {
//            var afrIdParts = afrCommit.currentAfr.afrId.split("_");
            var ecode = afrCommit.currentAfr.afrId;
            var dfsVerifiedRequest: CommitAfrRequest = {
                entityId: ecode,
                afrYear: afrCommit.currentAfr.year,
                afrToSave: null
            }
            const result = await http<CommitAfrRequest, Afr>({
                path: `/afr/verifiedByDFS`,
                method: 'post',
                body: dfsVerifiedRequest
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            console.log(JSON.stringify(ex));
            return null;
        }
    });

export const thirdPartyNotifyLGEs = createAsyncThunk(
    "AFR/ThirdPartyNotifyLGEs",
    //TODO what should the return value be
    async (reportReference: string): Promise<boolean | null> => {
        try {
            const result = await http<undefined, boolean>({
                path: `/afr/thirdPartyNotifyLGEs/${reportReference}`
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }


    });

export const adminEntityDocumentSave = createAsyncThunk(
    "AFR/AdminEntityDocumentSave",
    async (affiliateDocumentSave: EntityDocumentSave): Promise<DocumentRelationship | null> => {
        try {
            const result = await http<EntityDocumentSave, DocumentRelationship>({
                path: `/afr/saveEntityVersionRoleDocument`,
                method: 'post',
                body: affiliateDocumentSave
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const adminEntityAffiliateAuditSave = createAsyncThunk(
    "AFR/AdminEntityAffiliateAuditSave",
    async (affiliateDocumentSave: EntityDocumentSave): Promise<DocumentRelationship | null> => {
        try {
            const result = await http<EntityDocumentSave, DocumentRelationship>({
                path: `/afr/saveEntityAuditDocument`,
                method: 'post',
                body: affiliateDocumentSave
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const adminEntityAuditSave = createAsyncThunk(
    "AFR/AdminEntityAuditSave",
    async (affiliateDocumentSave: EntityDocumentSave): Promise<DocumentRelationship | null> => {
        try {
            const result = await http<EntityDocumentSave, DocumentRelationship>({
                path: `/afr/saveEntityAuditDocument`,
                method: 'post',
                body: affiliateDocumentSave
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

const updateReportingType_deprecated = function (currentAfr: Afr, rep: AffiliateReporting, newRep: AffiliateReporting, affiliateId: String) {


    // Now handle any additional changes connected to the change in reporting type
    var tbl = getTable(currentAfr, "uas_062_affiliate-totals");
    if (tbl) {
        var repTypeMember: DimensionReference | null = getRowDimensionReferenceFromTable(tbl, ReportingTypeAxisName, newRep.reportingType);
        if (!repTypeMember) {
            alert("missing reporting type member: " + newRep.reportingType);
        } else {

            var rowToUpdate: FactTableRow | null = null;
            for (var frow of tbl.rows) {
                const rowMember = getDimensionForRow(frow, "ReportingUnitAxis");
                //console.log(rowMember ? "Id = " + rowMember.id : "NO ROW MEMBER");

                if (rowMember && (rowMember.id === affiliateId)) {
                    rowToUpdate = frow;
                }
            }
            if (!rowToUpdate) {
                alert("could not find matching row for " + affiliateId);
                return;
            }
            var rowDims = rowToUpdate.dimensions.filter(dimref => dimref.axis.id === "ReportingUnitAxis");
            var newDimensions: Array<DimensionReference> = [
                {
                    ...rowDims[0]
                },
                repTypeMember
            ];
            //console.log(JSON.stringify(newDimensions));

            var newFacts: Array<Fact> | null = null;
            switch (newRep.reportingType) {
                case "INDEPEND": //"Independently Reported",
                case "NONCOMPO": //"Not a component unit",
                    newFacts = [];
                    break;

                case "BLENDED": //"Blended in Primary Report",
                case "DISCRETE": //"Discretely Reported",
                    break;

                case "ZEROREPT": //"Zero Revenues and Expenditures"
                    newFacts = [];
                    var cols = getColumnConceptsFromTable(tbl);

                    if (cols) {
                        for (var concept of cols) {
                            var zeroFact: Fact = {
                                value: 0,
                                textValue: null,
                                concept: {
                                    id: concept.id
                                },
                                dimensions: newDimensions,
                                validationerrors: [],
                                accountCode: concept.code
                            }
                            newFacts.push(zeroFact);
                        }
                    }
                    break;

            }
            // At a minimum we need to update the row dimensions to take the new value
            var newFactRows: any = [];
            for (var frow of tbl.rows) {
                const rowAffiliateMember = getDimensionForRow(frow, "ReportingUnitAxis");
                if (rowAffiliateMember && rowAffiliateMember.id === affiliateId) {
                    if (!newFacts) {
                        newFacts = [];
                        for (var f of frow.facts) {
                            newFacts.push({ ...f, dimensions: newDimensions });
                        }
                    }
                    var newRow = { account: frow.account, dimensions: newDimensions, facts: newFacts };
                    newFactRows.push(newRow);
                    //console.log("New Row for :", rowAffiliateMember.id);
                } else {
                    newFactRows.push({ ...frow });
                    //console.log("No change for :", rowAffiliateMember.id);
                }
            }

            tbl.rows = newFactRows;
        }
    }

}

// The reporting type is now a separately reported fact, not a dimension on affiliate facts so there's no fact fixup required if the reporting type changes
const updateReportingType = function (currentAfr: Afr, rep: AffiliateReporting, newRep: AffiliateReporting, affiliateId: String) {
}

function saveDocumentRelationshipFunction(state: any, action: any): any {
    var currentAfr: Afr | null = state.afr;
    if (!currentAfr) {
        // There's nothing to do
        return;
    }
    const document: DocumentRelationship = action.payload;

    if (!document) {
        return;
    }

    if (!(currentAfr && currentAfr.currentversion)) {
        return;
    }
    //TODO add new array if needed but don't use this return
    if (!currentAfr.currentversion.documents) {
        var arrDoc: Array<DocumentRelationship> = [];
        currentAfr.currentversion.documents = arrDoc;
    }

    const documents = currentAfr.currentversion.documents;
    var newDocs: Array<DocumentRelationship> = documents.filter(x => x.affiliate !== document.affiliate || x.role !== document.role);
    if (!document.documentId) {
        var prev = documents.filter(x => x.affiliate === document.affiliate && x.role === document.role && !x.documentId);
        if (prev.length === 1) {
            // The previous entry was a no-doc declaration - so treat this as a toggle and don't push the new relationship
        } else {
            // assert the new relationship - either there was no previous or it was for an actual document
            newDocs.push(document);
        }
    } else {
        // We are creating a new relationship - always push
        newDocs.push(document);
    }
    currentAfr.currentversion.documents = newDocs;
}

function saveAffiliateAuditDocumentRelationshipFunction(state: any, action: any): any {
    var currentAfr: Afr | null = state.afr;
    if (!currentAfr) {
        // There's nothing to do
        return;
    }
    const document: DocumentRelationship = action.payload;

    if (!document) {
        return;
    }

    if (!(currentAfr && currentAfr.currentversion)) {
        return;
    }

    if (currentAfr && currentAfr.affiliateReporting) {
        var affiliatedEntity = currentAfr.affiliateReporting.find(x => x.affiliateReference
            && (x.affiliateReference.code === document.affiliate));

        if (affiliatedEntity) {
            if (affiliatedEntity.audit) {
                //update existing audit with new document relationship
                affiliatedEntity.audit.auditPdf = document;
            } else {
                //create new audit and assign document relationship
                var newAudit: Audit = {
                    auditPdf: document,
                    auditorReference: null,
                    datePerformed: null,
                    dateReceived: null,
                    auditType: "AUDIT",
                    sendAuditPdf: null
                };

                affiliatedEntity.audit = newAudit;
            }
        }
    }
}

function saveEntityAuditDocumentRelationshipFunction(state: any, action: any): any {
    var currentAfr: Afr | null = state.afr;
    if (!currentAfr) {
        // There's nothing to do
        return;
    }
    const document: DocumentRelationship = action.payload;

    if (!document) {
        return;
    }

    if (!(currentAfr && currentAfr.currentversion)) {
        return;
    }

    if (currentAfr && currentAfr.currentversion) {

        if (currentAfr.currentversion.audit) {
            //update existing audit with new document relationship
            currentAfr.currentversion.audit.auditPdf = document;
        } else {
            //create new audit and assign document relationship
            var newAudit: Audit = {
                auditPdf: document,
                auditorReference: null,
                datePerformed: null,
                dateReceived: null,
                auditType: "AUDIT",
                sendAuditPdf: null
            };

            currentAfr.currentversion.audit = newAudit;
        }
    }
}





export const afrSlice = createSlice({
    name: 'Afr',
    initialState,
    reducers: {
        resetState: (state: any, action: any) => {
            return initialState
        },
        setToImportedAfr: (state: any, action: any) => {
            state.afr = action.payload;
            // NOTE - This call is also used in public reports to set up a shell Afr in report mode. The shell Afr does NOT have a currentVersion, so the following
            // processing should only take place for a full afr being returned form an actual import.
            if (state.afr && state.afr.currentversion && state.afr.currentversion.events) {
                // for an import any boolean state change events need to be added to the property changes set as though the user had created them
                var changes = createNullVersionState();
                state.afr.currentversion.events.map((e : any) => {
                    switch (e.state) {
                        case "GOVERNMENTAL_FUNDS_COMPLETE":
                            changes.workState.governmentalFundsComplete = e.statevalue;
                            break;

                        case "NO_GOVERNMENTAL_FUNDS":
                            changes.workState.noGovernmentalFunds = e.statevalue;
                            break;

                        case "PROPRIETARY_FUNDS_COMPLETE":
                            changes.workState.proprietaryFundsComplete = e.statevalue;
                            break;

                        case "NO_PROPRIETARY_FUNDS":
                            changes.workState.noProprietaryFunds = e.statevalue;
                            break;

                        case "FIDUCIARY_FUNDS_COMPLETE":
                            changes.workState.fiduciaryFundsComplete = e.statevalue;
                            break;

                        case "NO_FIDUCIARY_FUNDS":
                            changes.workState.noFiduciaryFunds = e.statevalue;
                            break;

                        case "COMPONENT_UNITS_FUNDS_COMPLETE":
                            changes.workState.componentUnitsFundsComplete = e.statevalue;
                            break;

                        case "NO_COMPONENT_UNITS_FUNDS":
                            changes.workState.noComponentUnitsFunds = e.statevalue;
                            break;

                        case "BALANCE_SHEET_COMPLETE":
                            changes.workState.balanceSheetComplete = e.statevalue;
                            break;

                        case "NO_BALANCE_SHEET_FUNDS":
                            changes.workState.noBalanceSheet = e.statevalue;
                            break;

                        case "REVENUES_COMPLETE":
                            changes.workState.revenuesComplete = e.statevalue;
                            break;

                        case "NO_REVENUES_FUNDS":
                            changes.workState.noRevenues = e.statevalue;
                            break;

                        case "EXPENDITURES_COMPLETE":
                            changes.workState.expendituresComplete = e.statevalue;
                            break;

                        case "NO_EXPENDITURES_FUNDS":
                            changes.workState.noExpenditures = e.statevalue;
                            break;

                        case "NO_CONSTRUCTION_PROJECTS":
                            changes.workState.noConstructionProjects = e.statevalue;
                            break;

                        case "NO_BONDS":
                            changes.workState.noBonds = e.statevalue;
                            break;

                    }
                });
                console.log(changes);
                state.versionPropertiesChanged = changes;
            }
        },
        setRequestDate: {
            reducer(state: any, action: any) {
                if (state.afr) {
                    state.afr.requestDate = action.payload;
                }
            },
            prepare(payload: string) {
                return {
                    payload
                }
            }
        },
        setAuditReceivedDate: {
            reducer(state: any, action: any) {
                if (state.afr) {
                    state.afr.auditReceivedDate = action.payload;
                }
            },
            prepare(payload: string) {
                return {
                    payload
                }
            }
        },
        setJLACDate: {
            reducer: (state: any, action: any) => {              
                if (action.payload.dateType == "WITHHOLDING_START_DATES") {
                    state.afr.currentversion.withholdingStartDates = action.payload.dateValue;
                } else if (action.payload.dateType == "BEGIN_ENFORCEMENT_LETTER_ISSUE_DATE") {
                    state.afr.currentversion.beginEnforcementLetterIssueDate = action.payload.dateValue;
                }
            },
            prepare(payload: JLACDate) {
                return {
                    payload
                };
            }
        },
        addNoDataFact: {
            reducer(state: any, action: any) {
                var currentAfr: Afr | null = state.afr;
                if (!currentAfr) {
                    return;
                }
                var category = action.payload;
                var ndFact: Fact | null = createNoDataFact(state.afr, category);
                if (ndFact) {
                    var tbl: any = null;
                    for (var tblEntry of currentAfr.currentversion.tables) {
                        if (tblEntry.structure.name === "uas_001b_afr-no-data-declarations") {
                            tbl = tblEntry;
                        }
                    }
                    if (tbl) {
                        tbl.rows[0].facts.push(ndFact);
                    }
                }
            },
            prepare(payload: string) {
                return {
                    payload
                }
            }

        },
        removeNoDataFact: {
            reducer(state: any, action: any) {
                var category = action.payload;
                    var tbl = getNoDataTable(state.afr);
                if (tbl) {
                        var newRows: FactTableRow[] = [];
                        for (var frow of tbl.rows) {
                            var newFacts: Fact[] = [];
                            for (var f of frow.facts) {
                                const matchId = utilityHelper.getRowSignature(f.concept.id, f.dimensions);
                                if (matchId.indexOf(category) < 0) {
                                    newFacts.push(f);
                                }
                            }
                            var newRow = { ...frow, facts: newFacts }
                            newRows.push(newRow);
                        }
                        tbl.rows = newRows;
                    }
            },
            prepare(payload: string) {
                return {
                    payload
                }
            }

        },
        updateAffiliateNoData: {
            reducer(state: any, action: any) {
                if (!state.afr) {
                    alert("no afr");
                    return;
                }
                var table = getTable(state.afr, enums.tableName.affiliateNodata);
                if (!table) {
                    return;
                }
                const { affiliate, propGroup, propName, boolValue } = action.payload;
                var noDataState = false;
                if (boolValue) {
                    noDataState = boolValue;
                }
                if (noDataState == false) {
                    if (!table.rows || table.rows.length === 0) {
                        // Nothing to do
                        return;
                    }
                }
                var afrArea = enums.nodataAreaName[propGroup + "_" + propName];
                var criteria: any = {
                    ReportingUnitAxis: affiliate,
                    AfrNoDataAxis: afrArea
                }
                var matchedFact = getMatchingFact(table, "AFRND_NoData", criteria);
                if (!matchedFact) {
                    if (noDataState === false) {
                        // We don't have a matched fact, and we are asserting false to NoData status - so just leave
                        return;
                    }
                }
                var newRows: FactTableRow[] = [];
                for (var frow of table.rows) {
                    const rowMember = getDimensionForRow(frow, "ReportingUnitAxis");

                    if (rowMember && (rowMember.code === affiliate)) {
                        var bFound = false;
                        var newFacts: any = [];
                        for (var fact of frow.facts) {
                            var areaDim = getDimensionForFact(fact, "AfrNoDataAxis");
                            if (areaDim) {
                                if (areaDim.id === afrArea) {
                                    // skip copying the fact. If the new value would be true it will be added to the set
                                    // otherwise this action will have the effect of removing the existing nodata face
                                    continue;
                                }
                            }
                            newFacts.push({ ...fact });
                        }
                        if (!bFound && noDataState === true) {
                            // We have to add in the relevant fact
                            var newDims: any = [];
                            for (var rd of frow.dimensions) {
                                newDims.push({ ...rd });
                            }
                            var colDim = getColumnDimensionReference(table, afrArea);
                            if (colDim) {
                                newDims.push({ ...colDim });
                            }
                            var rowConcept = frow.account;
                            if (rowConcept) {
                                var newFact: Fact = {
                                    dimensions: newDims,
                                    concept: { id: rowConcept.id },
                                    value: 1,
                                    textValue: null,
                                    accountCode: rowConcept.code,
                                    validationerrors: []
                                }
                                newFacts.push(newFact);
                            }
                        }
                        newRows.push({ ...frow, facts: newFacts });
                    } else {
                        // push the empty rows as well
                        newRows.push({ ...frow });
                    }
                }
                table.rows = newRows;
            },
            prepare(payload: noDataValue) {
                return {
                    payload
                }
            }
        },
        updateVersionStateValue: {
            reducer(state: any, action: any) {
                if (!state.afr) {
                    alert("no afr");
                    return;
                }
                const { propGroup, propName, boolValue, value } = action.payload;
                var changeProps: versionState | null = state.versionPropertiesChanged;
                if (!changeProps) {
                    changeProps = createNullVersionState();
                }
                var versionProps: versionState = state.afr.currentversion.versionState;
                if (value) {
                    versionProps.adminState.versionStatus = value;
                    changeProps.adminState.versionStatus = value;
                } else {
                    switch (propGroup) {
                        case "workState":
                            switch (propName) {
                                case "balanceSheetComplete":
                                    versionProps.workState.balanceSheetComplete = boolValue;
                                    changeProps.workState.balanceSheetComplete = boolValue;
                                    versionProps.reviewState.balanceSheetReviewed = null;
                                    changeProps.reviewState.balanceSheetReviewed = null;
                                    break;
                                case "revenuesComplete":
                                    versionProps.workState.revenuesComplete = boolValue;
                                    changeProps.workState.revenuesComplete = boolValue;
                                    versionProps.reviewState.revenuesReviewed = null;
                                    changeProps.reviewState.revenuesReviewed = null;
                                    break;
                                case "expendituresComplete":
                                    versionProps.workState.expendituresComplete = boolValue;
                                    changeProps.workState.expendituresComplete = boolValue;
                                    versionProps.reviewState.expendituresReviewed = null;
                                    changeProps.reviewState.expendituresReviewed = null;
                                    break;
                                case "governmentalFundsComplete":
                                    versionProps.workState.governmentalFundsComplete = boolValue;
                                    changeProps.workState.governmentalFundsComplete = boolValue;
                                    versionProps.reviewState.governmentalFundsReviewed = null;
                                    changeProps.reviewState.governmentalFundsReviewed = null;
                                    break;
                                case "componentUnitsFundsComplete":
                                    versionProps.workState.componentUnitsFundsComplete = boolValue;
                                    changeProps.workState.componentUnitsFundsComplete = boolValue;
                                    versionProps.reviewState.componentUnitsFundsReviewed = null;
                                    changeProps.reviewState.componentUnitsFundsReviewed = null;
                                    break;
                                case "proprietaryFundsComplete":
                                    versionProps.workState.proprietaryFundsComplete = boolValue;
                                    changeProps.workState.proprietaryFundsComplete = boolValue;
                                    versionProps.reviewState.proprietaryFundsReviewed = null;
                                    changeProps.reviewState.proprietaryFundsReviewed = null;
                                    break;
                                case "fiduciaryFundsComplete":
                                    versionProps.workState.fiduciaryFundsComplete = boolValue;
                                    changeProps.workState.fiduciaryFundsComplete = boolValue;
                                    versionProps.reviewState.fiduciaryFundsReviewed = null;
                                    changeProps.reviewState.fiduciaryFundsReviewed = null;
                                    break;
                                case "noBalanceSheet":
                                    versionProps.workState.noBalanceSheet = boolValue;
                                    changeProps.workState.noBalanceSheet = boolValue;
                                    if (!boolValue && versionProps.workState.balanceSheetComplete) {
                                        if (!existFactsInTable(state.afr, [enums.tableName.balanceSheet])) {
                                            versionProps.workState.balanceSheetComplete = null;
                                            changeProps.workState.balanceSheetComplete = null;
                                        }
                                    }
                                    break;
                                case "noRevenues":
                                    versionProps.workState.noRevenues = boolValue;
                                    changeProps.workState.noRevenues = boolValue;
                                    if (!boolValue && versionProps.workState.revenuesComplete) {
                                        if (!existFactsInTable(state.afr, [enums.tableName.revenues, enums.tableName.impactfees])) {
                                            versionProps.workState.revenuesComplete = null;
                                            changeProps.workState.revenuesComplete = null;
                                        }
                                    }
                                    break;
                                case "noExpenditures":
                                    versionProps.workState.noExpenditures = boolValue;
                                    changeProps.workState.noExpenditures = boolValue;
                                    if (!boolValue && versionProps.workState.expendituresComplete) {
                                        if (!existFactsInTable(state.afr, [enums.tableName.expenditures])) {
                                            versionProps.workState.expendituresComplete = null;
                                            changeProps.workState.expendituresComplete = null;
                                        }
                                    }
                                    break;
                                case "noExceptionsWaivers":
                                    versionProps.workState.noExceptionsWaivers = boolValue;
                                    changeProps.workState.noExceptionsWaivers = boolValue;
                                    break;
                                case "noConstructionProjects":
                                    versionProps.workState.noConstructionProjects = boolValue;
                                    changeProps.workState.noConstructionProjects = boolValue;
                                    break;
                                case "noBonds":
                                    versionProps.workState.noBonds = boolValue;
                                    changeProps.workState.noBonds = boolValue;
                                    break;
                                case "noGovernmentalFunds":
                                    versionProps.workState.noGovernmentalFunds = boolValue;
                                    changeProps.workState.noGovernmentalFunds = boolValue;
                                    break;
                                case "noProprietaryFunds":
                                    versionProps.workState.noProprietaryFunds = boolValue;
                                    changeProps.workState.noProprietaryFunds = boolValue;
                                    break;
                                case "noFiduciaryFunds":
                                    versionProps.workState.noFiduciaryFunds = boolValue;
                                    changeProps.workState.noFiduciaryFunds = boolValue;
                                    break;
                                case "noComponentUnitsFunds":
                                    versionProps.workState.noComponentUnitsFunds = boolValue;
                                    changeProps.workState.noComponentUnitsFunds = boolValue;
                                    break;

                            }
                            break;

                        case "reviewState":
                            switch (propName) {
                                case "auditReviewed":
                                    versionProps.reviewState.auditReviewed = boolValue;
                                    changeProps.reviewState.auditReviewed = boolValue;
                                    break;
                                case "balanceSheetReviewed":
                                    versionProps.reviewState.balanceSheetReviewed = boolValue;
                                    changeProps.reviewState.balanceSheetReviewed = boolValue;
                                    break;
                                case "certificationReviewed":
                                    versionProps.reviewState.certificationReviewed = boolValue;
                                    changeProps.reviewState.certificationReviewed = boolValue;
                                    break;
                                case "componentUnitsReviewed":
                                    versionProps.reviewState.componentUnitsReviewed = boolValue;
                                    changeProps.reviewState.componentUnitsReviewed = boolValue;
                                    break;
                                case "debtReviewed":
                                    versionProps.reviewState.debtReviewed = boolValue;
                                    changeProps.reviewState.debtReviewed = boolValue;
                                    break;
                                case "expendituresReviewed":
                                    versionProps.reviewState.expendituresReviewed = boolValue;
                                    changeProps.reviewState.expendituresReviewed = boolValue;
                                    break;
                                case "generalInfoReviewed":
                                    versionProps.reviewState.generalInfoReviewed = boolValue;
                                    changeProps.reviewState.generalInfoReviewed = boolValue;
                                    break;
                                case "revenuesReviewed":
                                    versionProps.reviewState.revenuesReviewed = boolValue;
                                    changeProps.reviewState.revenuesReviewed = boolValue;
                                    break;
                                case "supplementalReviewed":
                                    versionProps.reviewState.supplementalReviewed = boolValue;
                                    changeProps.reviewState.supplementalReviewed = boolValue;
                                    break;
                                case "cr939CourtInnovationsReviewed":
                                    versionProps.reviewState.cr939CourtInnovationsReviewed = boolValue;
                                    changeProps.reviewState.cr939CourtInnovationsReviewed = boolValue;
                                    break;
                                case "cr939LegalAidProgramReviewed":
                                    versionProps.reviewState.cr939LegalAidProgramReviewed = boolValue;
                                    changeProps.reviewState.cr939LegalAidProgramReviewed = boolValue;
                                    break;
                                case "cr939LawLibraryReviewed":
                                    versionProps.reviewState.cr939LawLibraryReviewed = boolValue;
                                    changeProps.reviewState.cr939LawLibraryReviewed = boolValue;
                                    break;
                                case "cr939JuvenileAlternativeProgramReviewed":
                                    versionProps.reviewState.cr939JuvenileAlternativeProgramReviewed = boolValue;
                                    changeProps.reviewState.cr939JuvenileAlternativeProgramReviewed = boolValue;
                                    break;
                                case "cr939SummaryReviewed":
                                    versionProps.reviewState.cr939SummaryReviewed = boolValue;
                                    changeProps.reviewState.cr939SummaryReviewed = boolValue;
                                    break;
                                case "cr29PartAReviewed":
                                    versionProps.reviewState.cr29PartAReviewed = boolValue;
                                    changeProps.reviewState.cr29PartAReviewed = boolValue;
                                    break;
                                case "cr29PartBReviewed":
                                    versionProps.reviewState.cr29PartBReviewed = boolValue;
                                    changeProps.reviewState.cr29PartBReviewed = boolValue;
                                    break;
                                case "cr29PartCReviewed":
                                    versionProps.reviewState.cr29PartCReviewed = boolValue;
                                    changeProps.reviewState.cr29PartCReviewed = boolValue;
                                    break;
                                case "cr29PartDReviewed":
                                    versionProps.reviewState.cr29PartDReviewed = boolValue;
                                    changeProps.reviewState.cr29PartDReviewed = boolValue;
                                    break;
                                case "cr29PartEReviewed":
                                    versionProps.reviewState.cr29PartEReviewed = boolValue;
                                    changeProps.reviewState.cr29PartEReviewed = boolValue;
                                    break;
                                case "cr29CertificationReviewed":
                                    versionProps.reviewState.cr29CertificationReviewed = boolValue;
                                    changeProps.reviewState.cr29CertificationReviewed = boolValue;
                                    break;
                                case "governmentalFundsReviewed":
                                    versionProps.reviewState.governmentalFundsReviewed = boolValue;
                                    changeProps.reviewState.governmentalFundsReviewed = boolValue;
                                    break;
                                case "componentUnitsFundsReviewed":
                                    versionProps.reviewState.componentUnitsFundsReviewed = boolValue;
                                    changeProps.reviewState.componentUnitsFundsReviewed = boolValue;
                                    break;
                                case "proprietaryFundsReviewed":
                                    versionProps.reviewState.proprietaryFundsReviewed = boolValue;
                                    changeProps.reviewState.proprietaryFundsReviewed = boolValue;
                                    break;
                                case "fiduciaryFundsReviewed":
                                    versionProps.reviewState.fiduciaryFundsReviewed = boolValue;
                                    changeProps.reviewState.fiduciaryFundsReviewed = boolValue;
                                    break;
                            }
                            break;

                        case "adminState":
                            switch (propName) {
                                case "adminReconciled":
                                    versionProps.adminState.adminReconciled = boolValue;
                                    changeProps.adminState.adminReconciled = boolValue;
                                    break;
                                case "jLACNonCompliance":

                                    versionProps.adminState.jLACNonCompliance = boolValue;
                                    changeProps.adminState.jLACNonCompliance = boolValue;
                                    break;
                            }
                            break;
                    }
                    state.versionPropertiesChanged = changeProps;
                }
            },
            prepare(payload: versionStateValue) {
                return {
                    payload
                }
            }
        },
        updateAffiliateReportingType: {
            reducer(state: any, action: any) {
                if (!state.afr) {
                    alert("no afr");
                    return;
                }
                const newRep = action.payload;
                if (!newRep.affiliateReference) {
                    return;
                }
                const affiliateId = newRep.affiliateReference.id;
                const currentAfr: Afr = state.afr;
                if (!currentAfr.affiliateReporting) {
                    return;
                }


                var newReporting: any = [];
                for (var rep of currentAfr.affiliateReporting) {
                    if (rep.affiliateReference && (rep.affiliateReference.id === affiliateId)) {
                        newReporting.push({ ...rep, reportingType: newRep.reportingType });
                    } else {
                        newReporting.push({ ...rep });
                    }
                }

                // Now handle any additional changes connected to the change in reporting type
                var tbl = getTable(currentAfr, "uas_062_affiliate-totals");
                if (tbl) {
                    var repTypeMember: DimensionReference | null = getRowDimensionReferenceFromTable(tbl, ReportingTypeAxisName, newRep.reportingType);
                    if (!repTypeMember) {
                        alert("missing reporting type member: " + newRep.reportingType);
                    } else {
                        var rowToUpdate: FactTableRow | null = null;
                        for (var frow of tbl.rows) {
                            const rowMember = getDimensionForRow(frow, "ReportingUnitAxis");
                            if (rowMember && (rowMember.id === affiliateId)) {
                                rowToUpdate = frow;
                            }
                        }
                        if (!rowToUpdate) {
                            alert("could not find matching row for " + affiliateId);
                            return;
                        }
                        var rowDims = rowToUpdate.dimensions.filter(dimref => dimref.axis.id === "ReportingUnitAxis");
                        var newDimensions: Array<DimensionReference> = [
                            {
                                ...rowDims[0]
                            },
                            repTypeMember
                        ];

                        var newFacts: Array<Fact> | null = null;
                        switch (newRep.reportingType) {
                            case "INDEPEND": //"Independently Reported",
                            case "NONCOMPO": //"Not a component unit",
                                newFacts = [];
                                break;

                            case "BLENDED": //"Blended in Primary Report",
                            case "DISCRETE": //"Discretely Reported",
                                break;

                            case "ZEROREPT": //"Zero Revenues and Expenditures"
                                newFacts = [];
                                var cols = getColumnConceptsFromTable(tbl);

                                if (cols) {
                                    for (var concept of cols) {
                                        var zeroFact: Fact = {
                                            value: 0,
                                            textValue: null,
                                            concept: {
                                                id: concept.id
                                            },
                                            dimensions: newDimensions,
                                            validationerrors: [],
                                            accountCode: concept.code
                                        }
                                        newFacts.push(zeroFact);
                                    }
                                }
                                break;

                        }
                        // At a minimum we need to update the row dimensions to take the new value
                        var newFactRows: any = [];
                        for (var frow of tbl.rows) {
                            const rowAffiliateMember = getDimensionForRow(frow, "ReportingUnitAxis");
                            if (rowAffiliateMember && rowAffiliateMember.id === affiliateId) {
                                if (!newFacts) {
                                    newFacts = [];
                                    for (var f of frow.facts) {
                                        newFacts.push({ ...f, dimensions: newDimensions });
                                    }
                                }
                                var newRow = { account: frow.account, dimensions: newDimensions, facts: newFacts };
                                newFactRows.push(newRow);
                            } else {
                                newFactRows.push({ ...frow });
                            }
                        }

                        tbl.rows = newFactRows;
                    }
                }

                currentAfr.affiliateReporting = newReporting;
            },
            prepare(payload: AffiliateReporting) {
                return {
                    payload
                }
            }
        },
        updateAffiliateReport: {
            reducer(state: any, action: any) {
                if (!state.afr) {
                    alert("no afr");
                    return;
                }
                const newRep = action.payload;
                if (!newRep.affiliateReference) {
                    // TODO we have a problem
                    return;
                }
                const currentAfr: Afr = state.afr;
                if (!currentAfr.affiliateReporting) {
                    return;
                }

                var newReporting: any = [];
                for (var rep of currentAfr.affiliateReporting) {
                    if (!rep.affiliateReference) {
                        // TODO We have a problem
                        continue;
                    }
                    if (rep.affiliateReference.code === newRep.affiliateReference.code) {
                        if (rep.reportingType !== newRep.reportingType) {
                            updateReportingType(currentAfr, rep, newRep, newRep.affiliateReference.id);
                        }
                        newReporting.push(newRep);
                    } else {
                        newReporting.push({ ...rep });
                    }
                }

                currentAfr.affiliateReporting = newReporting;
            },
            prepare(payload: AffiliateReporting) {
                return {
                    payload
                }
            }
        },
        updateAfrAudit: {
            reducer(state: any, action: any) {
                if (!state.afr) {
                    alert("no afr");
                    return;
                }
                const newAudit = action.payload;
                const currentAfr = state.afr;
                if (!currentAfr.currentversion) {
                    alert("no current version");
                }
                currentAfr.currentversion.audit = newAudit;
            },
            prepare(payload: Audit) {
                return {
                    payload
                }
            }
        },
        clearMatchingTableFacts: {
            reducer(state: any, action: any) {
                if (!state.afr) {
                    alert("no afr");
                    return;
                }
                const matchFacts: MatchTableFacts = action.payload;
                const tblName = matchFacts.tableName;
                const currentAfr = state.afr;
                if (!currentAfr.currentversion) {
                    alert("no current version");
                    return;
                }
                const tbl = getTable(currentAfr, tblName);
                if (tbl) {
                    const matchId = utilityHelper.getRowSignature(matchFacts.matchConcept.conceptId, matchFacts.matchConcept.dimensions);
                    var newRows: FactTableRow[] = [];
                    if (matchId.indexOf("|") < 0) {
                        // There are no dimension checks
                        tbl.rows.map(ftr => utilityHelper.getRowSignature(ftr.account != null ? ftr.account.id : "", null) === matchId ? null : newRows.push({ ...ftr }));
                    } else {
                        tbl.rows.map(ftr => utilityHelper.getRowSignature(ftr.account != null ? ftr.account.id : "", ftr.dimensions) === matchId ? null : newRows.push({ ...ftr }));
                    }
                    tbl.rows = newRows;
                }
            },
            prepare(payload: MatchTableFacts) {
                return {
                    payload
                }
            }
        },
        clearTableFacts: {
            reducer(state: any, action: any) {
                if (!state.afr) {
                    alert("no afr");
                    return;
                }
                const tblName = action.payload;
                const currentAfr = state.afr;
                if (!currentAfr.currentversion) {
                    alert("no current version");
                }
                const tbl = getTable(currentAfr, tblName);
                if (tbl) {
                    tbl.rows = [];
                }
            },
            prepare(payload: string) {
                return {
                    payload
                }
            }
        },
        clearTableFactRows: {
            reducer(state: any, action: any) {
                if (!state.afr) {
                    alert("no afr");
                    return;
                }
                const tblName = action.payload;
                const currentAfr = state.afr;
                if (!currentAfr.currentversion) {
                    alert("no current version");
                }
                const tbl = getTable(currentAfr, tblName);
                if (tbl) {
                    var newRows: Array<FactTableRow> = [];
                    for (var row of tbl.rows) {
                        var newRow: any = { ...row, facts: [] };
                        newRows.push(newRow);
                    }
                    tbl.rows = newRows;
                }
            },
            prepare(payload: string) {
                return {
                    payload
                }
            }
        },
        clearFactsForAffiliate: {
            reducer(state: any, action: any) {
                // Remove all facts for an affiliate (required when we change reporting type to independent after accumulating facts while dependent)
                if (!state.afr) {
                    alert("no afr");
                    return;
                }
                const currentAfr = state.afr;
                if (!currentAfr.currentversion) {
                    alert("no current version");
                }

                const affiliateId = action.payload;
                if (!affiliateId || affiliateId.length === 0) {
                    return;
                }
                if (!currentAfr.affiliateReporting) {
                    return;
                }

                var affiliateCode = "0";
                for (var rep of currentAfr.affiliateReporting) {
                    if (!rep.affiliateReference) {
                        // TODO We have a problem
                        continue;
                    }
                    if (rep.affiliateReference.id === affiliateId) {
                        affiliateCode = rep.affiliateReference.code;
                    }
                }

                // Remove all facts for an affiliate (required when we change reporting type to independent after accumulating facts while dependent)
                var affiliateRowTableNames: string[] = [
                    enums.tableName.affiliatedEntity,
                    enums.tableName.affiliatecompensation,
                    enums.tableName.affiliateNoData,
                    enums.tableName.affiliateprojects
                ];
                affiliateRowTableNames.map(function (tblName, index, array) {
                    const tbl = getTable(currentAfr, tblName);
                    if (tbl) {
                        var newRows: Array<FactTableRow> = [];
                        for (var row of tbl.rows) {
                            const rowMember = getDimensionForRow(row, "ReportingUnitAxis");
                            if (!rowMember) {
                                continue;
                            }
                            var newRow;
                            if (rowMember.id === affiliateId) {
                                // This is the row containing facts matching the affiliate
                                switch (tblName) {
                                    case enums.tableName.affiliatedEntity:
                                    case enums.tableName.affiliatecompensation:
                                        // For a fixed row table reset the facts array to an empty array
                                        newRow = { ...row, facts: [] };
                                        newRows.push(newRow);
                                        break;

                                    default:
                                        // For a dynamic table, drop the row altogether
                                        break;
                                }
                            } else {
                                newRow = { ...row }
                                newRows.push(newRow);
                            }
                        }
                        tbl.rows = newRows;
                    }
                });
                // Now clear out any documents tied to this affiliate
                // NOTE - Document relationships are currently being stored using entity codes rather than entity ids.
                if (currentAfr.currentversion.documents && currentAfr.currentversion.documents.length > 0) {
                    var newDocuments: DocumentRelationship[] = [];
                    currentAfr.currentversion.documents.map(function (docrel: DocumentRelationship, index: any, arr: any) {
                        if (docrel.affiliate === affiliateCode) {
                            // Don't add to the new list of relationships
                        } else {
                            newDocuments.push({ ...docrel });
                        }
                    });

                    currentAfr.currentversion.documents = newDocuments;
                }
            },
            prepare(payload: string) {
                return {
                    payload
                }
            }
        },
        updateTableFact: {
            reducer(state: any, action: any) {
                if (!state.afr) {
                    alert("no afr");
                    return;
                }

                const { tableId, rowId, fundGroup, concept, value } = action.payload;
                // NOTE - the fundGroup passed in here is one of the following possible value types
                // * fundgroup member id (not code) for a standard fundgroup table
                // * concept id for a flipped table (e.g. affiliate report)
                // * "period" for a dimensionless table (e.g. Long term debt)
                // The only use for member codes (as opposed to ids) is in calculating row ids, and the
                // row id is supplied in this update call
                const currentAfr = state.afr;
                const table = getTable(currentAfr, tableId);
                if (!table) {
                    alert("no table found for " + tableId);
                } else {
                    const columnAxisId = getColumnAxisId(table);
                    const row = getRow(table, rowId);
                    var addFacts: Fact[] = [];
                    if (!row) {
                        alert("no row found for " + rowId);
                    } else {
                        var iValue = 0;
                        var valueNaN = true;
                        if (value.length > 0) {
                            iValue = parseInt(value);
                            valueNaN = false;
                        }
                        var foundFact = getFact(row, fundGroup);
                        if (foundFact) {
                            if (valueNaN) {
                                // remove the fact from the list
                                if (fundGroup === "period") {
                                    // This is a one column table - just empty the row
                                    row.facts = [];
                                } else {
                                    row.facts = row.facts.filter(function (v, index, arr) {
                                        return !matchFactByFund(v, columnAxisId, fundGroup);
                                    });
                                }
                            } else {
                                foundFact.value = iValue;
                            }
                        } else {
                            // NOTE - For the affiliates flipped table the fundGroup and concept are both set to the account id
                            var bNotFlipped = (fundGroup && fundGroup !== "period" && fundGroup !== concept && fundGroup.length > 0);
                            var colDim: DimensionReference | null = bNotFlipped ? getColumnDimensionReference(table, fundGroup) : null;
                            if (bNotFlipped && !colDim) {
                                if (fundGroup === "period") {
                                    var newNoDimFact: any = {
                                        value: iValue,
                                        dimensions: [],
                                        concept: { name: "", code: "", id: concept },
                                        validationerrors: [],
                                        accountCode: rowId
                                    }
                                    row.facts.push(newNoDimFact);
                                    addFacts.push(newNoDimFact);
                                } else {
                                    alert("did not find fund group " + fundGroup);
                                }
                            } else {
                                var fDims: Array<DimensionReference> = [];
                                if (bNotFlipped && colDim) {
                                    // This is NOT a flipped table, so we add the column dimension
                                    var colAxis = table.structure.columnHierarchy.axes[0];
                                    fDims.push(colDim);
                                }
                                if (row.dimensions != null && row.dimensions.length > 0) {
                                    for (var dr of row.dimensions) {
                                        var drClone = { ...dr };
                                        fDims.push(drClone);
                                    }
                                }
                                var newFact: any = {
                                    value: iValue,
                                    dimensions: fDims,
                                    concept: { name: "", code: "", id: concept },
                                    validationerrors: [],
                                    accountCode: rowId
                                }
                                row.facts.push(newFact);
                                addFacts.push(newFact);
                            }
                        }
                    }
                }
            },
            prepare(payload: factValue) {
                return {
                    payload
                }
            },

        },
        changeColumnDimension: {
            reducer(state: any, action: any) {
                if (!state.afr) {
                    alert("no afr");
                    return;
                }

                const { tableId, sliceId, axisId, prevId, newId } = action.payload;
                // NOTE - the fundGroup passed in here is one of the following possible value types
                // * fundgroup member id (not code) for a standard fundgroup table
                // * concept id for a flipped table (e.g. affiliate report)
                // * "period" for a dimensionless table (e.g. Long term debt)
                // The only use for member codes (as opposed to ids) is in calculating row ids, and the
                // row id is supplied in this update call
                const currentAfr = state.afr;
                const table = getTable(currentAfr, tableId);
                if (!table) {
                    alert("no table found for " + tableId);
                    return;
                }
                var changeRows: FactTableRow[] = [];
                var newRows: FactTableRow[] = [];
                if (sliceId) {
                    // copy all the rows that do NOT match the sliceId
                    for (var prevRow of table.rows) {
                        var prevRowId = getRowId(prevRow);
                        if (prevRowId.indexOf(sliceId) < 0) {
                            newRows.push({ ...prevRow });
                        } else {
                            changeRows.push(prevRow);
                        }
                    }
                } else {
                    changeRows = table.rows;
                }
                for (var changeRow of changeRows) {
                    // Modify those facts that have the previous value for a member
                    var modRow: FactTableRow = { ...changeRow };
                    var newRowFacts: Fact[] = [];
                    for (var fact of changeRow.facts) {
                        var modFact = { ...fact };
                        var modDimensions: any = [];
                        for (var dr of fact.dimensions) {
                            var modDimension = { ...dr };
                            if (modDimension.member != null && dr.member !== null && dr.axis.id === axisId && dr.member.id === prevId) {
                                modDimension.member.code = newId;
                                modDimension.member.id = newId;
                            }
                            modDimensions.push(modDimension);
                        }
                        modFact.dimensions = modDimensions;
                        newRowFacts.push(modFact);
                    }
                    modRow.facts = newRowFacts;
                    newRows.push(modRow);
                }
                table.rows = newRows;
            },
            prepare(payload: bulkReassignColumnDimension) {
                return {
                    payload
                }
            },
        },
        replaceTableFacts: {
            reducer(state: any, action: any) {
                if (!state.afr) {
                    alert("no afr");
                    return;
                }

                const { tableId, fundGroup, sliceId, minAccount, maxAccount, facts } = action.payload;
                // NOTE - the fundGroup passed in here is one of the following possible value types
                // * fundgroup member id (not code) for a standard fundgroup table
                // * concept id for a flipped table (e.g. affiliate report)
                // * "period" for a dimensionless table (e.g. Long term debt)
                // The only use for member codes (as opposed to ids) is in calculating row ids, and the
                // row id is supplied in this update call
                const currentAfr = state.afr;
                const table = getTable(currentAfr, tableId);
                if (!table) {
                    alert("no table found for " + tableId);
                    return;
                }
                var columnAspectFilter: string | null = null;
                var rowidFilter: string = "";
                var rowAccountMap: any = null;
                if (table.structure) {
                    if (table.structure.rows && table.structure.rows.length > 0) {
                        rowAccountMap = {};
                        table.structure.rows.map(possibleRow => {
                            var acc: Account = {
                                code: possibleRow.code,
                                name: possibleRow.name,
                                id: possibleRow.id
                            }
                            rowAccountMap[possibleRow.code] = acc;
                        });
                    }
                    if (fundGroup && table.structure.nthlevelcolumnHierarchy) {
                        for (var tbe of table.structure.nthlevelcolumnHierarchy) {
                            if (tbe.label === fundGroup) {

                                if (tbe.sublevels && tbe.sublevels.length > 0) {
                                    for (var tbeCol of tbe.sublevels) {
                                        if (!tbeCol.dimensionaspect || !tbeCol.dimensionaspect.member) {
                                            continue;
                                        }
                                        if (!columnAspectFilter) {
                                            columnAspectFilter = tbeCol.dimensionaspect.member.code;
                                        } else {
                                            columnAspectFilter += "," + tbeCol.dimensionaspect.member.code;
                                        }
                                    } 
                                } else {
                                    var dimCode = "na";
                                    if (tbe.dimensionaspect.member) {
                                        dimCode = tbe.dimensionaspect.member.code;
                                    }
                                    if (!columnAspectFilter) {
                                        columnAspectFilter = dimCode;
                                    } else {
                                        columnAspectFilter += "," + dimCode;
                                    }

                                }
                            }
                        }
                        // Now create the rowid filter
                        for (var newReplaceRow of facts) {
                            if (rowidFilter.length > 0) {
                                rowidFilter += ";" + newReplaceRow.rowId;
                            } else {
                                rowidFilter = newReplaceRow.rowId;
                            }
                        }
                    }
                }
                var newRows: FactTableRow[] = [];
                if (sliceId) {
                    // copy all the rows that do NOT match the sliceId
                    for (var prevRow of table.rows) {
                        var prevId = getRowId(prevRow);
                        if (prevId.indexOf(sliceId) < 0) {
                            newRows.push({ ...prevRow });
                        }
                    }
                }
                var rowMap: any = {};
                var rowCandidatesForFundGroupFilter: FactTableRow[] = [];
                if (minAccount) {
                    // This is for Balance Sheet tables where each UI table covers a subset of rows from the underlying Balance Sheet table.
                    // copy all the rows that are outside the min and max account filter
                    for (var prevRow of table.rows) {
                        var rowAccount = prevRow.account;
                        if (rowAccount) {
                            if (rowAccount.code < minAccount || rowAccount.code > maxAccount) {
                                newRows.push({ ...prevRow });
                            } else if (columnAspectFilter) {
                                rowCandidatesForFundGroupFilter.push(prevRow);
                            }
                        }
                    }
                } else {
                    // This is for every other table
                    if (columnAspectFilter) {
                        rowCandidatesForFundGroupFilter = table.rows;
                    }
                }
                if (columnAspectFilter) {
                    for (var candidateRow of rowCandidatesForFundGroupFilter) {
                        var prevRowId = getRowId(candidateRow);
                        if (rowidFilter.length > 0 && rowidFilter.indexOf(prevRowId) < 0) {
                            // If the existing row doesn't match any of the incoming fact rows, then they must contains facts from outside the fundgroup range
                            // so just push them to the new rows 
                            newRows.push({ ...candidateRow });
                        } else {
                            var nonMatchedFacts: Fact[] = [];
                            for (var prevFact of candidateRow.facts) {
                                // NOTE - Its OK to filter here by the dimension code because these are existing facts in the AFR and will have fully realized
                                // dimension aspects that includes the code.
                                if (!factHasAspect(prevFact, columnAspectFilter)) {
                                    nonMatchedFacts.push({ ...prevFact });
                                }
                            }
                            var prowCopy = { ...candidateRow, facts: nonMatchedFacts };
                            newRows.push(prowCopy);
                            rowMap[prevRowId] = prowCopy;
                        }
                    }
                }
                // Now add the new rows if any
                for (var newValue of facts) {
                    var newRow: FactTableRow | null = null;
                    if (rowMap.hasOwnProperty(newValue.rowId)) {
                        newRow = rowMap[newValue.rowId] as FactTableRow;
                    } else {
                        var rowDims: any = [];
                        var dimRank = 1;
                        var values = newValue.rowId.split("|");
                        var accountCode = values[0];
                        if (table.structure.rowHierarchy.axes) {
                            for (var rowAxis of table.structure.rowHierarchy.axes) {
                                var dvalue = values[dimRank++];
                                if (dvalue === "?") {
                                    dvalue = "";
                                }
                                var newRef: any = null;
                                if (rowAxis.typedDomain) {
                                    newRef = getTypedMemberReference(rowAxis, rowAxis.typedDomain, dvalue);
                                } else {
                                    newRef = getMemberReference(rowAxis, dvalue);
                                }
                                if (newRef) {
                                    rowDims.push(newRef);
                                }
                            }
                        }
                        if (sliceId) {
                            var sliceRef = getSliceMemberReference(table.structure, sliceId);
                            if (sliceRef) {
                                rowDims.push(sliceRef);
                            }
                        }
                        if (rowAccountMap && rowAccountMap.hasOwnProperty(accountCode)) {
                            newRow = {
                                account: rowAccountMap[accountCode],
                                dimensions: rowDims,
                                facts: []
                            }
                        } else {
                            newRow = {
                                account: null,
                                dimensions: rowDims,
                                facts: []
                            }
                        }
                        newRows.push(newRow);
                        rowMap[newValue.rowId] = newRow;
                    }
                    var factDims: DimensionReference[] = [];
                    var colDim = getColumnDimensionReference(table, newValue.fundGroup);
                    if (colDim) {
                        factDims.push(colDim);
                    }
                    if (newRow.dimensions) {
                        for (var rdim of newRow.dimensions) {
                            factDims.push({ ...rdim });
                        }
                    }
                    var newFact: any = {
                        value: Number(newValue.value),
                        dimensions: factDims,
                        concept: { name: "", code: "", id: newValue.concept },
                        validationerrors: [],
                        accountCode: newValue.rowId
                    }
                    newRow.facts.push(newFact);
                }
                table.rows = newRows;
            },
            prepare(payload: bulkFactValues) {
                return {
                    payload
                }
            },

        },
        removeUnassignedFact: {
            reducer(state: any, action: any) {
                if (!state.afr) {
                    alert("no afr");
                    return;
                }
                if (!state.afr.currentversion) {
                    alert("no version");
                    return;
                }
                if (!state.afr.currentversion.unassignedFacts) {
                    // This is OK but nothing to do
                    return;
                }
                const { accountCode, dimensionalSignature, concept, value } = action.payload;
                // NOTE - the dimensional signature is currently empty, but the account code should sufficiently encapsulate the dimensional codes to cover most cases
                // This assumption may need to be revisited in the event of multiple facts sharing the same account code that are in the unassigned group. The worst case
                // is that this call removes all of them - which may not be a problem
                state.afr.currentversion.unassignedFacts = state.afr.currentversion.unassignedFacts.filter(function (v: any, index: any, arr: any) {
                    return v.accountCode !== accountCode;
                });
            },
            prepare(payload: unassignedFactValue) {
                return {
                    payload
                }
            },

        },
        addDocumentRelationship: {
            reducer(state: any, action: any) {
                var docRel = action.payload;
                if (!state.afr || !state.afr.currentversion) {
                    return;
                }
                var version = state.afr.currentversion;
                if (docRel.role === "AUDIT") {
                    if (docRel.affiliate) {
                        if (state.afr.affiliateReporting) {
                            var affRep: AffiliateReporting | undefined = state.afr.affiliateReporting.find((x : AffiliateReporting) => x.affiliateReference ? x.affiliateReference.code === docRel.affiliate : false );
                            if (affRep != undefined) {
                                if (!affRep.audit) {
                                    var newAffAudit: Audit = {
                                        auditorReference: null,
                                        auditPdf: docRel,
                                        datePerformed: null,
                                        dateReceived: null,
                                        auditType: "AUDIT",
                                        sendAuditPdf: null
                                    }
                                    affRep.audit = newAffAudit;
                                } else {
                                    affRep.audit.auditPdf = docRel;
                                }
                            }
                        }
                    } else {
                        if (!version.audit) {
                            var newAudit: Audit = {
                                auditorReference: null,
                                auditPdf: docRel,
                                datePerformed: null,
                                dateReceived: null,
                                auditType: "AUDIT",
                                sendAuditPdf: null
                            }
                            version.audit = newAudit;
                        } else {
                            version.audit.auditPdf = docRel;
                        }
                    }
                    return;
                }
                var newDocs: DocumentRelationship[] = [];
                var overwrite: boolean = false;
                if (version.documents) {
                    for (var docrow of version.documents) {
                        if (docRel.role === docrow.role && docrow.affiliate === docRel.affiliate ) {
                            newDocs.push(docRel);
                            overwrite = true;
                        } else {
                            newDocs.push(docrow);
                        }
                    }
                }
                if (overwrite === false) {
                    newDocs.push(docRel);
                }
                version.documents = newDocs;
            },
            prepare(payload: DocumentRelationship) {
                return {
                    payload
                }
            }

        },
        createAccount: (state: any, action: any) => {
            const newAcc: NewAccount = action.payload;
            const currentAfr = state.afr;
            if (!currentAfr) {
                alert("No afr for createAccount");
                return;
            }
            const table = getTable(currentAfr, newAcc.tableId);
            if (!table) {
                alert("No table for createAccount");
                return;
            }

            
            var allowDups = allowsDuplicateAccounts(table);
            //This check is already being performed in the respective component.
            //The modal of said component may have to factor in the fund group.  We don't have access to the
            //fund group here so this check should not be done here
            //if (!allowDups) {
            //    const accountId = getRowIdForAccountAndDimensions(newAcc.account, newAcc.dimensions);
            //    const row = getRow(table, accountId);
            //    if (row) {
            //        alert("Account already exists!");
            //        return;
            //    }
            //}
            var newrow: FactTableRow = {
                account: newAcc.account,
                dimensions: newAcc.dimensions,
                facts: []

            };
            table.rows.push(newrow);
            //order the table by account
            if (allowDups) {
                const orderByAccount = function compare(a: FactTableRow, b: FactTableRow) {
                    var row_a = getRowIdForAccountAndDimensions(a.account, a.dimensions);
                    var row_b = getRowIdForAccountAndDimensions(b.account, b.dimensions);
                    if (row_a < row_b) {
                        return -1;
                    }
                    if (row_a > row_b) {
                        return 1;
                    }
                    return 0;
                }

                table.rows.sort(orderByAccount);
            } else {
                const orderByAccount = function compare(a: FactTableRow, b: FactTableRow) {
                    var row_a = getRowId(a);
                    var row_b = getRowId(b);
                    if (row_a < row_b) {
                        return -1;
                    }
                    if (row_a > row_b) {
                        return 1;
                    }
                    return 0;
                }

                table.rows.sort(orderByAccount);
            }

        },

        saveCertification: (state: any, action: any) => {
            if (!state.afr) {
                return;
            }
            const updatedCertification: Certification = action.payload;
            const currentAfr: Afr = state.afr;

            var newCertification = {
                financialEmergency: updatedCertification.financialEmergency,
                compliance218: updatedCertification.compliance218,
                financialDeterioration: updatedCertification.financialDeterioration,
                submittedDocumentation: updatedCertification.submittedDocumentation,
                icertify: updatedCertification.icertify,
                firstCertifier: updatedCertification.firstCertifier,
                secondCertifier: updatedCertification.secondCertifier
            }

            if (updatedCertification.firstCertifier != null && newCertification.firstCertifier != null) {
                newCertification.firstCertifier.agree = (updatedCertification.firstCertifier.name.trim().length > 0 && updatedCertification.firstCertifier.title.trim().length > 0);
            }
            if (updatedCertification.secondCertifier != null && newCertification.secondCertifier != null) {
                newCertification.secondCertifier.agree = (updatedCertification.secondCertifier.name.trim().length > 0 && updatedCertification.secondCertifier.title.trim().length > 0);
            }

            if (currentAfr.currentversion) {
                currentAfr.currentversion.certification = newCertification;
            }

        },
        saveDocumentRelationship: saveDocumentRelationshipFunction,
        deleteDocument: (state: any, action: any) => {
            const currentAfr: Afr | null = state.afr;
            if (!currentAfr) {
                // There's nothing to do
                return;
            }
            const document: DocumentReferenceInput = action.payload;

            if (!document) {
                return;
            }
            if (!currentAfr) {
                return;
            }

            if (document.role === enums.documentRole.audit) {
                /* remove the document from affilliated entities or AFRAudit */
                if (currentAfr && currentAfr.affiliateReporting) {
                    for (var ae of currentAfr.affiliateReporting) {
                        if (ae.audit && ae.audit.auditPdf && ae.audit.auditPdf.documentId === document.documentId) {
                            ae.audit.auditPdf = null;
                            return;
                        }
                    }
                }
                if (currentAfr && currentAfr.currentversion && currentAfr.currentversion.audit && currentAfr.currentversion.audit.auditPdf && currentAfr.currentversion.audit.auditPdf.documentId === document.documentId) {
                    currentAfr.currentversion.audit.auditPdf = null;
                    return;
                }

            } else {

                const documents = currentAfr.currentversion.documents;
                var index = getFirstDocumentIndexFromRole(currentAfr, document.role, document.affiliate);
                if (index > -1) {
                    documents.splice(index, 1);
                }
            }
        },
        createXAmendment: (state: any, action: any) => {
            if (!state.afr) {
                return;
            }
            const currentAfr: Afr = state.afr;

            if (!currentAfr) {
                alert("No afr for createAmendment");
                return;
            }
            // ******************************************************
            // todo: We should have an array of versions to save 
            // the previous AFR.
            // code goes here.  
            // ******************************************************
            currentAfr.status = "IN_PROGRESS";
            currentAfr.currentversion.versionNumber = currentAfr.currentversion.versionNumber + 1;
            if (currentAfr.currentversion.amendmentNumber) {
                currentAfr.currentversion.amendmentNumber = currentAfr.currentversion.amendmentNumber + 1;
            } else {
                currentAfr.currentversion.amendmentNumber = 1;
            }

        },
        ensureInProgress: (state: any, action: any) => {
            if (!state.afr) {
                return;
            }
            const currentAfr: Afr = state.afr;

            // ******************************************************
            // todo: We should have an array of versions to save 
            // the previous AFR.
            // code goes here.  
            // ******************************************************
            if (currentAfr.status === "NOT_REPORTED") {
                currentAfr.status = "IN_PROGRESS";
            }
        }
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(loadAfr.pending, (state: any) => {
                state.status = "loadingAfr";
            })
            .addCase(loadAfr.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.afr = action.payload;
            })
            .addCase(commitAfr.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(commitAfr.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                if (action.payload.status === "ERROR") {
                    // Don't update the slice if the status of the payload is UNKNOWN
                    return;
                }
                state.afr = action.payload;
            })
            .addCase(createAmendment.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(createAmendment.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.afr = action.payload;
            })
            .addCase(deleteCurrentVersion.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(deleteCurrentVersion.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.afr = action.payload;
            })
            .addCase(returnByDFS.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(returnByDFS.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.afr = action.payload;
            })
            .addCase(verifiedByDFS.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(verifiedByDFS.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.afr = action.payload;
            })
            .addCase(adminEntityDocumentSave.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(adminEntityDocumentSave.fulfilled, saveDocumentRelationshipFunction)

            .addCase(adminEntityAffiliateAuditSave.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(adminEntityAffiliateAuditSave.fulfilled, saveAffiliateAuditDocumentRelationshipFunction)
            .addCase(adminEntityAuditSave.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(adminEntityAuditSave.fulfilled, saveEntityAuditDocumentRelationshipFunction)
    },
});


export const { resetState, setToImportedAfr, updateTableFact, replaceTableFacts, clearTableFacts,
    clearTableFactRows, clearFactsForAffiliate, updateAffiliateReport, updateAffiliateReportingType, updateAfrAudit, updateAffiliateNoData,
    saveCertification, createAccount, updateVersionStateValue, removeUnassignedFact, saveDocumentRelationship, addDocumentRelationship, deleteDocument, changeColumnDimension,
    clearMatchingTableFacts, setRequestDate, setAuditReceivedDate, addNoDataFact, removeNoDataFact, ensureInProgress, setJLACDate } = afrSlice.actions;

export const getSelectedAfr = (state: RootState) => state.afr ? state.afr.afr : null;

export const getMissingFacts = (state: RootState) => (state.afr && state.afr.afr && state.afr.afr.currentversion) ? state.afr.afr.currentversion.missingFacts : [];

export const getVersionPropertiesChanged = (state: RootState) => state.afr ? state.afr.versionPropertiesChanged : createNullVersionState();

export const getCommitAfr = (state: RootState) => (state.afr && state.afr.afr) ? getAfrVersionToCommit(state.afr.afr, state.afr.versionPropertiesChanged, false) : null;

export default afrSlice.reducer;

export const isCRAAuditRequired = createSelector(
    (state: RootState) => state.afr ? state.afr.afr : null,
    (state: RootState) => state.affiliate ? state.affiliate.selectedAffiliatedEntityCode : "",
    (currentAfr: Afr | null, selectedAffiliatedEntityCode: string) => {

        if (!(currentAfr && selectedAffiliatedEntityCode.length > 0)) { return false }
        var isCRA: boolean = false;

        var currentAffiliatedEntity: AffiliateReporting | null | undefined = getSelectedAffiliateReport(currentAfr, selectedAffiliatedEntityCode);

        isCRA = ((currentAffiliatedEntity && currentAffiliatedEntity.affiliateReference && currentAffiliatedEntity.affiliateReference.category === CRA) ? true : false);

        // check if amount revenues are greater than 100.000
        if (!isCRA) {
            return false;
        }

        var affTable: FactTable | null;
        var row: any = null;
        affTable = getTable(currentAfr, enums.tableName.affiliatedEntity);

        // get affiliated entity row in redux
        if (affTable) {
            row = getRow(affTable, selectedAffiliatedEntityCode);
        }

        if (row) {
            // get the column information for revenues
            var factRevenues = getFact(row, enums.affiateEntityColumn.revenues);
            var factExpenditures = getFact(row, enums.affiateEntityColumn.expenditures);
            if (factRevenues || factExpenditures) {
                if ((factRevenues && factRevenues.value > 100000) || (factExpenditures && factExpenditures.value > 100000)) {
                    if (currentAffiliatedEntity && currentAffiliatedEntity.audit) {
                        var audit: Audit = currentAffiliatedEntity.audit;

                        if (audit.datePerformed && audit.auditorReference) {
                            return false;
                        } else {
                            return true
                        }
                    } else {
                        return true;
                    }
                };
            }
        }
        return false;
    }
);

export const isAFRAuditRequired = createSelector(
    (state: RootState) => state.afr ? state.afr.afr : null,
    (state: RootState) => state.entity ? state.entity.entity : null,
    (afr: Afr | null, entity: Entity | null) => {
        var response: AuditMessage = { requireAudit: false, message: "" };

        if (!afr) {
            response = {
                requireAudit: false,
                message: `There is no Afr currently selected.`
            };
            return response;
        }
        if (!entity) {
            response = {
                requireAudit: false,
                message: `There is no Entity currently selected.`
            };
            return response;
        }
        var ageOfLastAudit: number | null = afr.lastPriorAuditYear ? afr.year - afr.lastPriorAuditYear : null;
        var totalRevenues = utilityHelper.getRevenuesTotal(afr);
        var totalExpenditures = utilityHelper.getExpendituresTotal(afr);
        var totalImpactFees = utilityHelper.getImpactFeesTotal(afr);
        var impactFeesText = "";

        if (totalImpactFees !== 0) {
            impactFeesText = "(Including Impact Fees)";
        }
        let options: Intl.NumberFormatOptions = {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        };
        const numberFormat = new Intl.NumberFormat('en-US', options);
        var totalRevenuesFormatted = numberFormat.format(totalRevenues);
        var totalExpendituresFormatted = numberFormat.format(totalExpenditures);



        /* Audit is required for counties */
        if (entity.typecode === enums.entityType.county) {
            response = { requireAudit: true, message: "All counties require an Audit." };
            return response;
        } else {
            if (entity.typecode === enums.entityType.city) {
                // more that 250.000 requires an audit
                if (totalRevenues >= 250000 || totalExpenditures >= 250000) {
                    response = {
                        requireAudit: true,
                        //message: `Cities need to have an Audit when the total revenues or expenditures are greater than $250,000. You have reported ${totalRevenuesFormatted} in revenues ${impactFeesText} and ${totalExpendituresFormatted} in expenditures for this AFR.`
                        message: `This AFR requires an audit per 218.39 (1)(i), F.S.`
                    };
                    return response;
                } else {
                    // between 100.000 and 250.000 requires an audit every 3 years.
                    if (totalRevenues >= 100000 || totalExpenditures >= 100000) {
                        if (!ageOfLastAudit) {
                            response = {
                                requireAudit: true,
                                message: `This AFR requires an audit per 218.39 (1)(i), F.S.`
                            };
                            return response;
                        } else if (ageOfLastAudit > 3) {
                            response = {
                                requireAudit: true,
                                message: `This AFR requires an audit per 218.39 (1)(i), F.S.`
                            };
                            return response;
                        } else {
                            response.message = `Cities must have an audit completed when the total revenues or expenditures are between $100,000 and $250,000 and the city has not had an audit in the 2 preceding years. Your pior audit was completed in fiscal year ${afr.lastPriorAuditYear}.`
                            return response;
                        }
                    } else {
                        return response;
                    }
                }
            } else {
                if (entity.typecode === enums.entityType.specialDistrict || entity.typecode === enums.entityType.other) {
                    // more than 100.000 requires an audit
                    if (totalRevenues >= 100000 || totalExpenditures >= 100000) {
                        response = {
                            requireAudit: true,
                            //message: `Special districts and Other entities need to have an Audit when the total revenues or expenditures are greater than $100,000. You have reported ${totalRevenuesFormatted} in revenues ${impactFeesText} and ${totalExpendituresFormatted} in expenditures for this AFR.`
                            message: `This AFR requires an audit per 218.39 (1)(i), F.S.`
                        };
                        return response;
                    } else {
                        // between 50 and 100 requires an audit every 3 years
                        if (totalRevenues >= 50000 || totalExpenditures >= 50000) {
                            if (!ageOfLastAudit) {
                                response = {
                                    requireAudit: true,
                                    message: `This AFR requires an audit per 218.39 (1)(i), F.S.`
                                };
                                return response;
                            } else if (ageOfLastAudit > 3) {
                                response = {
                                    requireAudit: true,
                                    message: `This AFR requires an audit per 218.39 (1)(i), F.S.`
                                };
                                return response;
                            } else {
                                response.message = `This AFR does not require an audit per 218.39 (1)(i), F.S. If an audit was performed, it can be reported. Otherwise, a Data Element Worksheet will be generated.`
                                return response;
                            }
                        } else {
                            return response;
                        }
                    }
                }
            }

        }

    }

);

export const pullDocumentList = createSelector(
    (state: RootState) => state.afr ? state.afr.afr : null,
    (afr: Afr | null) => {
        if (!afr) {
            return [];
        }
        return getDocumentList(afr);

    }

);

export const pullEventList = createSelector(
    (state: RootState) => state.afr ? state.afr.afr : null,
    (state: RootState) => state.events ? state.events.selectedEvents ? state.events.selectedEvents : null : null,
    (afr: Afr | null, afrEvents: AfrEvents | null) => {

        var eventList: Array<EventGeneric> = [];

        if (afr && afrEvents) {
            /* retrieve the list of version changes */
            eventList = getEventList(afr);

            /* retrieve the list of comments */
            var comments = getCommentEventList(afrEvents);
            for (var comment of comments) {
                eventList.push(comment);
            }

            /* retrieve the list of messages */
            var messages = getMessageEventList(afrEvents);
            for (var msg of messages) {
                eventList.push(msg);
            }

            /* sort the events ordered by timestamp descending */
            eventList.sort((a, b) => (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0));
        }

        return eventList;

    }

);

/*
   This selector merges the information from the AFRSlice and the MiscSlice for Affiliate reporting object 
 */
export const getMergedAEs = createSelector(
    (state: RootState) => state.afr ? (state.afr.afr ? state.afr.afr.affiliateReporting : null) : null,
    (state: RootState) => state.misc ? state.misc.affiliates : null,
    (afrAE: Array<AffiliateReporting>, miscAE: Array<AffiliateReporting>) => {

        var merged: Array<AffiliateReporting> = [];

        if (afrAE === null) {
            return null;
        }
        if (miscAE === null) {
            return afrAE;
        }

        if (miscAE.length === 0) {
            return afrAE;
        }

        var index: number;
        for (let ae of afrAE) {
            if (ae.affiliateReference) {
                var aeCode = ae.affiliateReference.code;
                index = miscAE.findIndex(x => x.affiliateReference ? x.affiliateReference.code === aeCode : false);
                if (index > -1) {
                    var miscReporting = miscAE.find(x => x.affiliateReference ? x.affiliateReference.code === aeCode : false);
                    if (miscReporting) {
                        merged.push(miscReporting);
                    }
                } else {
                    merged.push(ae);
                }
            }
        }

        return merged; 

    }

);

export const selectAfrStateStatus = createDraftSafeSelector(
    (state: RootState) => state,
    (state: any) => state.afr.status
);



